import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import { getMailPassword } from '../utils/mailCredentials';
import { isCompactComposeView, isTouchDevice } from '../utils/composeView';
import {
  CONTACT_SEARCH_DEBOUNCE_MS,
  CONTACT_SEARCH_LIMIT,
  filterGroupsByQuery,
  mapContactToSuggestion,
  mapGroupToSuggestion,
} from '../utils/recipientSuggestions';
import ConfirmationModal from './ConfirmationModal';
import EmojiPicker from 'emoji-picker-react';
import UserAvatar from './UserAvatar';
import ContactHoverCard from './ContactHoverCard';
import './ComposeMail.css';
import './ContactHoverCard.css';
import {
  enhanceHtmlLinks,
  hasMarkdownLinks,
  normalizeLinkUrl,
  plainTextToHtmlBody,
} from '../utils/mailLinks';

const escapeHtml = (str) =>
  String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/** Decode once if IMAP / headers arrived HTML-entity encoded (prevents &quot; in From line). */
const decodeCommonEntities = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str
    .replace(/&nbsp;/gi, ' ')
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');
};

/**
 * HTML → plain text. Preserves hyperlink URLs (Drive / Docs / shared links)
 * so forwards do not drop "Document Name" without the actual share URL.
 */
const htmlToPlainText = (html) => {
  if (!html || typeof html !== 'string') return '';
  if (!html.includes('<')) return html;
  try {
    const d = document.createElement('div');
    d.innerHTML = html;
    d.querySelectorAll('a[href]').forEach((anchor) => {
      const href = String(anchor.getAttribute('href') || '').trim();
      const text = String(anchor.textContent || '').trim();
      if (!href || href.startsWith('#') || href.toLowerCase().startsWith('javascript:')) {
        return;
      }
      // Keep mailto: as-is text; keep http(s) / drive links with URL visible.
      let replacement = text || href;
      if (/^https?:\/\//i.test(href) && text && text !== href && !text.includes(href)) {
        replacement = `${text} (${href})`;
      } else if (/^https?:\/\//i.test(href) && !text) {
        replacement = href;
      }
      anchor.replaceWith(document.createTextNode(replacement));
    });
    return (d.textContent || d.innerText || '').replace(/\u00a0/g, ' ').trim();
  } catch {
    return html
      .replace(/<a\b[^>]*href=["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi, (_, href, label) => {
        const text = String(label || '').replace(/<[^>]+>/g, '').trim();
        if (/^https?:\/\//i.test(href) && text && text !== href) return `${text} (${href})`;
        return text || href;
      })
      .replace(/<[^>]+>/g, ' ')
      .trim();
  }
};

const nlToBr = (escapedText) => escapedText.replace(/\n/g, '<br>');

/** Gmail-style: "Sat, May 9, 2026 at 2:50 PM" */
const formatGmailStyleDate = (dateValue) => {
  if (!dateValue) return '';
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return String(dateValue);
  return d.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/** Split "On ... wrote:" thread tail into quoted block (Gmail-like). */
const splitLeadingQuotedBlock = (text) => {
  const t = String(text || '').replace(/\r\n/g, '\n');
  const lines = t.split('\n');
  const wroteRe = /^On .+ wrote:$/i;
  let idx = -1;
  for (let i = 0; i < lines.length; i++) {
    if (wroteRe.test(lines[i].trim())) {
      idx = i;
      break;
    }
  }
  if (idx === -1) return { main: t.trim(), quote: '' };
  const main = lines.slice(0, idx).join('\n').trimEnd();
  const quote = lines.slice(idx).join('\n').trim();
  return { main, quote };
};

const getBodyTextContent = (html) => {
  if (!html || typeof html !== 'string') return '';
  try {
    const d = document.createElement('div');
    d.innerHTML = html;
    return (d.textContent || '').replace(/\u00a0/g, ' ').trim();
  } catch {
    return html.replace(/<[^>]+>/g, ' ').trim();
  }
};

const looksLikeHtmlBody = (value) => typeof value === 'string' && /<[a-z][\s\S]*>/i.test(value);

const buildSendBodyPayload = (rawBody) => {
  const trimmed = String(rawBody || '').trim();
  if (!trimmed) return { body: '', html: undefined };
  if (looksLikeHtmlBody(trimmed)) {
    const html = enhanceHtmlLinks(trimmed);
    return { body: htmlToPlainText(html), html };
  }
  if (hasMarkdownLinks(trimmed)) {
    const html = enhanceHtmlLinks(plainTextToHtmlBody(trimmed));
    return { body: htmlToPlainText(html), html };
  }
  return { body: trimmed, html: undefined };
};

const RECIPIENT_EMAIL_REGEX = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const parseRecipientList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return [...new Set(value.map((entry) => String(entry).trim().toLowerCase()).filter(Boolean))];
  }
  return [
    ...new Set(
      String(value)
        .split(',')
        .map((entry) => entry.trim().toLowerCase())
        .filter(Boolean)
    ),
  ];
};

const formatRecipientList = (recipients) => recipients.join(', ');

const placeCaretAtComposeStart = (el) => {
  if (!el) return;
  const range = document.createRange();
  const sel = window.getSelection();
  const first = el.firstChild;
  if (first && first.firstChild && first.firstChild.nodeType === Node.TEXT_NODE) {
    range.setStart(first.firstChild, 0);
  } else if (first) {
    range.setStart(first, 0);
  } else {
    range.setStart(el, 0);
  }
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
};

/** Parse "Name <email@x.com>" / '"Name" <e>' / plain email for Gmail-style lines */
const parseMailboxDisplay = (raw) => {
  const t = decodeCommonEntities(String(raw || '').trim());
  if (!t) return { name: '', email: '', displayHtml: '' };
  const angleOnly = t.match(/^<([^>]+)>$/);
  if (angleOnly) {
    const email = angleOnly[1].trim();
    const safeEmail = escapeHtml(email);
    return { name: '', email, displayHtml: `<span style="color:#202124;">&lt;${safeEmail}&gt;</span>` };
  }
  const angle = t.match(/^(?:"([^"]+)"|([^<]+?))\s*<([^>]+)>\s*$/);
  if (angle) {
    const name = (angle[1] || angle[2] || '').trim();
    const email = angle[3].trim();
    const safeEmail = escapeHtml(email);
    if (name) {
      const safeName = escapeHtml(name);
      return {
        name,
        email,
        displayHtml: `<strong style="font-weight:700;color:#202124;">${safeName}</strong> <span style="color:#202124;">&lt;${safeEmail}&gt;</span>`,
      };
    }
    return { name: '', email, displayHtml: `<span style="color:#202124;">&lt;${safeEmail}&gt;</span>` };
  }
  if (/^[^\s<>]+@[^\s<>]+$/.test(t)) {
    const safeEmail = escapeHtml(t);
    return { name: '', email: t, displayHtml: `<span style="color:#202124;">&lt;${safeEmail}&gt;</span>` };
  }
  return { name: t, email: '', displayHtml: `<span style="color:#202124;">${escapeHtml(t)}</span>` };
};

/**
 * Gmail-like forwarded block (inline styles so recipients see the same in Gmail / other clients).
 */
/**
 * Keep safe markup from an original HTML body for forwarding so Drive/Docs
 * share links (<a href="...">) remain clickable for the next recipient.
 */
const sanitizeForwardHtmlFragment = (html) => {
  if (!html || typeof html !== 'string') return '';
  try {
    const d = document.createElement('div');
    d.innerHTML = html;
    d.querySelectorAll('script,style,iframe,object,embed,link,meta').forEach((el) => el.remove());
    d.querySelectorAll('*').forEach((el) => {
      [...el.attributes].forEach((attr) => {
        const name = attr.name.toLowerCase();
        const value = String(attr.value || '');
        if (name.startsWith('on') || name === 'srcdoc') {
          el.removeAttribute(attr.name);
          return;
        }
        if ((name === 'href' || name === 'src') && /^\s*javascript:/i.test(value)) {
          el.removeAttribute(attr.name);
        }
      });
    });
    // Ensure anchors open correctly for shared Drive docs.
    d.querySelectorAll('a[href]').forEach((a) => {
      const href = String(a.getAttribute('href') || '').trim();
      if (/^https?:\/\//i.test(href)) {
        a.setAttribute('target', '_blank');
        a.setAttribute('rel', 'noopener noreferrer');
      }
    });
    return d.innerHTML.trim();
  } catch {
    return '';
  }
};

const buildGmailForwardBodyHtml = (originalMail) => {
  const originalFrom = decodeCommonEntities(originalMail.from || '');
  const originalTo = decodeCommonEntities(originalMail.to || '');
  const originalSubject = decodeCommonEntities(originalMail.subject || '');
  const originalDateValue = originalMail.createdAt || originalMail.date;
  const dateStr = formatGmailStyleDate(originalDateValue);

  const bodyLooksHtml = (value) => typeof value === 'string' && /<[a-z][\s\S]*>/i.test(value);
  const originalHtml =
    (originalMail.html && String(originalMail.html).trim()) ||
    (bodyLooksHtml(originalMail.body) ? String(originalMail.body).trim() : '');
  const safeHtml = sanitizeForwardHtmlFragment(originalHtml);

  const plainSource =
    (originalMail.text && String(originalMail.text).trim()) ||
    htmlToPlainText(originalMail.html || originalMail.body || '') ||
    (!bodyLooksHtml(originalMail.body) ? String(originalMail.body || '').trim() : '') ||
    '';
  const { main, quote } = splitLeadingQuotedBlock(plainSource);

  const ff = 'font-family:Roboto,Helvetica,Arial,sans-serif;';
  const metaKey =
    'color:#5f6368;font-size:14px;font-weight:500;line-height:1.5;margin-right:6px;';
  const metaRow = `margin:0 0 4px;line-height:1.5;${ff}`;

  const fromParsed = parseMailboxDisplay(originalFrom);
  const fromLine =
    fromParsed.displayHtml ||
    `<span style="color:#202124;font-size:14px;">${escapeHtml(originalFrom)}</span>`;

  const toParsed = parseMailboxDisplay(originalTo);
  const toLine =
    toParsed.displayHtml ||
    `<span style="color:#202124;font-size:14px;">${escapeHtml(originalTo)}</span>`;

  const subjectText = String(originalSubject || '').trim();
  const subjectInner = subjectText
    ? escapeHtml(subjectText)
    : '<span style="color:#9aa0a6;font-size:14px;">&nbsp;</span>';

  const boxStyle =
    'border:1px solid #dadce0;border-radius:8px;background:#ffffff;max-height:360px;overflow-y:auto;' +
    'overflow-x:auto;padding:12px 14px;margin:10px 0 0;font-size:14px;line-height:1.55;color:#202124;' +
    ff;

  const parts = [];
  parts.push(`<div style="min-height:10px;"><br></div>`);
  parts.push(
    `<div style="font-size:14px;color:#202124;margin:0 0 12px;${ff}">---------- Forwarded message ----------</div>`
  );
  parts.push(`<div style="${metaRow}"><span style="${metaKey}">From:</span>${fromLine}</div>`);
  parts.push(
    `<div style="${metaRow}"><span style="${metaKey}">Date:</span><span style="color:#202124;font-size:14px;">${escapeHtml(dateStr)}</span></div>`
  );
  parts.push(
    `<div style="${metaRow}"><span style="${metaKey}">Subject:</span><span style="color:#202124;font-size:14px;">${subjectInner}</span></div>`
  );
  parts.push(`<div style="${metaRow}"><span style="${metaKey}">To:</span>${toLine}</div>`);

  const innerBits = [];
  if (safeHtml) {
    // Keep original HTML (incl. Drive / Docs share anchors) so forward recipients can open them.
    innerBits.push(`<div style="word-wrap:break-word;">${safeHtml}</div>`);
  } else {
    if (main) {
      innerBits.push(
        `<div style="white-space:pre-wrap;word-wrap:break-word;">${nlToBr(escapeHtml(main))}</div>`
      );
    }
    if (quote) {
      innerBits.push(
        `<blockquote type="cite" style="margin:14px 0 0;padding:0 0 0 12px;border-left:3px solid #dadce0;color:#5f6368;font-size:14px;line-height:1.55;white-space:pre-wrap;word-wrap:break-word;">${nlToBr(
          escapeHtml(quote)
        )}</blockquote>`
      );
    }
  }
  if (!innerBits.length) {
    innerBits.push(
      `<div style="color:#9aa0a6;font-size:13px;font-style:italic;">${escapeHtml('(No message text)')}</div>`
    );
  }
  parts.push(`<div style="${boxStyle}">${innerBits.join('')}</div>`);

  return parts.join('');
};

const getForwardAttachmentAuthHeaders = (isServerMail) => {
  const headers = {};
  const token = localStorage.getItem('token');
  const mailPassword = getMailPassword();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (isServerMail && mailPassword) headers['x-mail-password'] = mailPassword;
  return headers;
};

const fetchForwardAttachmentFile = async ({
  mailId,
  attachmentIndex,
  attachmentMeta,
  isServerMail,
}) => {
  const partIndex = attachmentMeta?.index ?? attachmentIndex;
  const apiBase = process.env.REACT_APP_API_URL || '';
  const url = isServerMail
    ? `${apiBase}/api/mail/server/message/${encodeURIComponent(mailId)}/attachment/${partIndex}?download=1`
    : `${apiBase}/api/mail/${encodeURIComponent(mailId)}/attachment/${partIndex}?download=1`;

  const response = await fetch(url, { headers: getForwardAttachmentAuthHeaders(isServerMail) });
  if (!response.ok) {
    throw new Error(`Failed to fetch attachment: ${attachmentMeta?.filename || partIndex}`);
  }

  const blob = await response.blob();
  const filename = attachmentMeta?.filename || `attachment-${attachmentIndex + 1}`;
  const mimetype = attachmentMeta?.mimetype || attachmentMeta?.contentType || blob.type || 'application/octet-stream';
  return new File([blob], filename, { type: mimetype });
};

const isInlineCidOnlyAttachment = (attachmentMeta) => {
  const disposition = String(attachmentMeta?.contentDisposition || '').toLowerCase();
  const cid = attachmentMeta?.cid;
  // Keep real file attachments; skip tiny cid inline images that are already in HTML body.
  return Boolean(cid) && disposition.includes('inline');
};

const loadForwardAttachments = async (originalMail, mailId, isServerMail) => {
  const attachments = (originalMail?.attachments || []).filter(
    (meta) => !isInlineCidOnlyAttachment(meta)
  );
  if (!attachments.length) return { files: [], failed: 0, total: 0 };

  const results = await Promise.all(
    attachments.map(async (attachmentMeta, attachmentIndex) => {
      try {
        const file = await fetchForwardAttachmentFile({
          mailId,
          attachmentIndex: attachmentMeta.index ?? attachmentIndex,
          attachmentMeta,
          isServerMail,
        });
        return { file, failed: false };
      } catch (error) {
        console.error('Error loading forward attachment:', attachmentMeta?.filename, error);
        return { file: null, failed: true };
      }
    })
  );

  const files = results.map((r) => r.file).filter(Boolean);
  const failed = results.filter((r) => r.failed).length;
  return { files, failed, total: attachments.length };
};

/**
 * ComposeMail Component
 * Gmail-style compose interface with reply and forward support
 */
const ComposeMail = ({ currentUser, draft = null, onClearDraft }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const groupId = useMemo(() => new URLSearchParams(location.search).get('groupId') || '', [location.search]);
  const isGroupCompose = Boolean(groupId);
  const [groupInfo, setGroupInfo] = useState(null);

  const composeHeaderTitle = useMemo(() => {
    const p = new URLSearchParams(location.search);
    if (p.get('groupId')) return 'Group Message';
    if (p.get('forward') || p.get('forwardUid')) return 'Forward';
    if (p.get('reply') || p.get('replyUid')) return 'Reply';
    return 'New Message';
  }, [location.search]);

  const [toRecipients, setToRecipients] = useState(() => parseRecipientList(draft?.to || new URLSearchParams(location.search).get('to')));
  const [toInput, setToInput] = useState('');
  const [cc, setCc] = useState(draft?.cc ? (Array.isArray(draft.cc) ? draft.cc.join(', ') : draft.cc) : '');
  const [bcc, setBcc] = useState(draft?.bcc ? (Array.isArray(draft.bcc) ? draft.bcc.join(', ') : draft.bcc) : '');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [subject, setSubject] = useState(draft?.subject || '');
  const [body, setBody] = useState(draft?.body || '');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isSending, setIsSending] = useState(false);
  const [sendToast, setSendToast] = useState(null); // { status: 'sending' | 'sent' | 'error', text }
  const [draftId, setDraftId] = useState(draft?._id || null);
  const [draftStatus, setDraftStatus] = useState('');
  const [loadingOriginalMail, setLoadingOriginalMail] = useState(false);
  const bodyTextareaRef = useRef(null);
  const autosaveTimerRef = useRef(null);
  const discardRequestedRef = useRef(false);
  const undoResolveRef = useRef(null);
  const undoTimerRef = useRef(null);
  const sentToastTimerRef = useRef(null);
  const suppressSentToastRef = useRef(false);
  const savedCursorPositionRef = useRef(null);
  const [isBodyFocused, setIsBodyFocused] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [windowSize, setWindowSize] = useState('normal'); // 'minimized', 'normal', 'expanded'
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [showSendOptions, setShowSendOptions] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [showFormatting, setShowFormatting] = useState(false);
  const [selectedFont, setSelectedFont] = useState('Sans Serif');
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkText, setLinkText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showConfidentialModal, setShowConfidentialModal] = useState(false);
  const [confidentialExpiration, setConfidentialExpiration] = useState('1 week');
  const [confidentialPasscode, setConfidentialPasscode] = useState('no-sms');
  const [showInsertPhotoModal, setShowInsertPhotoModal] = useState(false);
  const [photoTab, setPhotoTab] = useState('google-photos');
  const [photoSubTab, setPhotoSubTab] = useState('photos');
  const [photoSearch, setPhotoSearch] = useState('');
  const [showSignatureDropdown, setShowSignatureDropdown] = useState(false);
  const [selectedSignature, setSelectedSignature] = useState('no-signature');
  const [showMoreOptionsDropdown, setShowMoreOptionsDropdown] = useState(false);
  const [showTextSizeDropdown, setShowTextSizeDropdown] = useState(false);
  const [selectedTextSize, setSelectedTextSize] = useState('normal');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [selectedTextColor, setSelectedTextColor] = useState('#000000');
  const [selectedBgColor, setSelectedBgColor] = useState('#ffffff');
  const [showAlignmentDropdown, setShowAlignmentDropdown] = useState(false);
  const [selectedAlignment, setSelectedAlignment] = useState('left');
  const [showFormattingMoreOptions, setShowFormattingMoreOptions] = useState(false);
  const [activeRecipientField, setActiveRecipientField] = useState('');
  const [recipientSuggestions, setRecipientSuggestions] = useState([]);
  const [recipientMeta, setRecipientMeta] = useState({});
  const [savingContactEmail, setSavingContactEmail] = useState('');
  const [hoveredRecipientEmail, setHoveredRecipientEmail] = useState('');
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const toRecipientInputRef = useRef(null);
  const selectingSuggestionRef = useRef(false);
  const recipientSuggestionsRef = useRef([]);
  const recipientHoverTimeoutRef = useRef(null);
  const [isMobileView, setIsMobileView] = useState(() => isCompactComposeView());
  const [showMobileRecipientDetails, setShowMobileRecipientDetails] = useState(false);

  const isComposeEmpty = useMemo(() => {
    const bodyWithoutSignature = String(body || '').replace(
      /<div[^>]*data-atozas-signature="1"[^>]*>[\s\S]*?<\/div>/g,
      ''
    );
    return (
      !toRecipients.length &&
      !toInput.trim() &&
      !subject.trim() &&
      !getBodyTextContent(bodyWithoutSignature) &&
      !cc.trim() &&
      !bcc.trim()
    );
  }, [toRecipients, toInput, subject, body, cc, bcc]);

  useEffect(() => {
    recipientSuggestionsRef.current = recipientSuggestions;
  }, [recipientSuggestions]);

  useEffect(() => {
    setSelectedSuggestionIndex(-1);
  }, [recipientSuggestions]);

  useEffect(() => {
    if (isGroupCompose) return undefined;

    const syncKey = 'atozas_contacts_synced';
    if (sessionStorage.getItem(syncKey)) return undefined;

    let cancelled = false;
    axiosInstance
      .post('/api/contacts/sync')
      .then(() => {
        if (!cancelled) sessionStorage.setItem(syncKey, '1');
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [isGroupCompose]);

  useEffect(() => {
    const handleResize = () => setIsMobileView(isCompactComposeView());
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  const renderComposeMoreMenu = () => {
    const menu = (
      <>
        {isMobileView && (
          <div
            className="compose-mobile-menu-backdrop"
            onClick={() => setShowMoreOptionsDropdown(false)}
            aria-hidden="true"
          />
        )}
        <div className={`more-options-dropdown ${isMobileView ? 'compose-mobile-more-menu' : ''}`} role="menu">
          <button
            type="button"
            className="more-options-item"
            role="menuitem"
            onClick={() => {
              setShowMoreOptionsDropdown(false);
              handleScheduleSend();
            }}
          >
            <span>Schedule send</span>
          </button>
          <button
            type="button"
            className="more-options-item"
            role="menuitem"
            onClick={() => {
              setShowMoreOptionsDropdown(false);
              setActiveRecipientField('to');
              bodyTextareaRef.current?.blur();
              requestAnimationFrame(() => toRecipientInputRef.current?.focus());
            }}
          >
            <span>Add from Contacts</span>
          </button>
          <button
            type="button"
            className="more-options-item"
            role="menuitem"
            onClick={() => {
              setShowMoreOptionsDropdown(false);
              setShowConfidentialModal(true);
            }}
          >
            <span>Confidential mode</span>
          </button>
          <button
            type="button"
            className={`more-options-item${isComposeEmpty ? ' disabled' : ''}`}
            role="menuitem"
            disabled={isComposeEmpty || isSending}
            onClick={() => {
              if (isComposeEmpty) return;
              setShowMoreOptionsDropdown(false);
              saveDraft();
            }}
          >
            <span>Save draft</span>
          </button>
          <button
            type="button"
            className="more-options-item"
            role="menuitem"
            onClick={() => {
              setShowMoreOptionsDropdown(false);
              handleDiscard();
            }}
          >
            <span>Discard</span>
          </button>
          <button
            type="button"
            className="more-options-item"
            role="menuitem"
            onClick={() => {
              setShowMoreOptionsDropdown(false);
              navigate('/account');
            }}
          >
            <span>Settings</span>
          </button>
          <button
            type="button"
            className="more-options-item"
            role="menuitem"
            onClick={() => setShowMoreOptionsDropdown(false)}
          >
            <span>Help and feedback</span>
          </button>
        </div>
      </>
    );

    if (isMobileView && typeof document !== 'undefined') {
      return createPortal(menu, document.body);
    }
    return menu;
  };

  const renderSuggestionResult = (field, suggestion, index = -1) => {
    const isGroupSuggestion = suggestion.suggestionType === 'group';
    const displayName = isGroupSuggestion
      ? suggestion.name
      : suggestion.displayName || suggestion.username || suggestion.email;
    const isSelected = index >= 0 && index === selectedSuggestionIndex;

    return (
      <button
        type="button"
        className={`recipient-suggestion-item${isSelected ? ' is-selected' : ''}`}
        key={`${suggestion.suggestionType || 'contact'}-${suggestion._id || suggestion.email}`}
        onMouseDown={handleSuggestionPointerDown(field, suggestion)}
        onPointerDown={handleSuggestionPointerDown(field, suggestion)}
        onTouchEnd={handleSuggestionPointerDown(field, suggestion)}
        onMouseEnter={() => setSelectedSuggestionIndex(index)}
      >
        {isGroupSuggestion ? (
          <span className="recipient-suggestion-avatar recipient-suggestion-group-avatar">
            {String(suggestion.name || 'G').charAt(0).toUpperCase()}
          </span>
        ) : (
          <UserAvatar
            user={suggestion}
            className="recipient-suggestion-avatar"
            size="sm"
            title={displayName}
          />
        )}
        <span className="recipient-suggestion-copy">
          <strong>{displayName}</strong>
          <small>
            {isGroupSuggestion
              ? `${suggestion.members?.length || 0} members label`
              : suggestion.email}
          </small>
        </span>
      </button>
    );
  };

  const renderToSuggestionsPanel = () => {
    if (!isMobileView) return null;
    if (!shouldShowRecipientSuggestions('to')) return null;

    return (
      <div className="compose-mobile-recipient-suggestions">
        <div className="recipient-suggestions recipient-suggestions-mobile compose-mobile-typeahead">
          {recipientSuggestions.map((suggestion, index) => renderSuggestionResult('to', suggestion, index))}
        </div>
      </div>
    );
  };

  const renderGroupComposeSuggestions = () => null;

  // Get font family based on selected font
  const getFontSize = (size) => {
    switch(size) {
      case 'small': return '12px';
      case 'normal': return '14px';
      case 'large': return '18px';
      case 'huge': return '24px';
      default: return '14px';
    }
  };

  const getFontFamily = (font) => {
    const fontMap = {
      'Sans Serif': 'Arial, Helvetica, sans-serif',
      'Serif': 'Georgia, "Times New Roman", serif',
      'Fixed Width': '"Courier New", Courier, monospace',
      'Wide': 'Arial, sans-serif',
      'Narrow': 'Arial Narrow, sans-serif',
      'Comic Sans MS': '"Comic Sans MS", cursive',
      'Garamond': 'Garamond, serif',
      'Georgia': 'Georgia, serif',
      'Tahoma': 'Tahoma, Geneva, sans-serif',
      'Trebuchet MS': '"Trebuchet MS", sans-serif',
      'Verdana': 'Verdana, Geneva, sans-serif',
    };
    return fontMap[font] || 'Arial, sans-serif';
  };

  /**
   * Handle Bold formatting
   */
  const handleBold = () => {
    document.execCommand('bold', false, null);
    bodyTextareaRef.current?.focus();
  };

  /**
   * Handle Italic formatting
   */
  const handleItalic = () => {
    document.execCommand('italic', false, null);
    bodyTextareaRef.current?.focus();
  };

  /**
   * Handle Underline formatting
   */
  const handleUnderline = () => {
    document.execCommand('underline', false, null);
    bodyTextareaRef.current?.focus();
  };

  /**
   * Generate color swatches for color picker
   */
  const generateColorSwatches = () => {
    // Grayscale row (10 colors from black to white)
    const grayscale = [
      '#000000', '#2d2d2d', '#434343', '#5c5c5c', '#666666',
      '#808080', '#999999', '#b7b7b7', '#d9d9d9', '#ffffff'
    ];

    // Bright colors row (10 vibrant colors)
    const brightColors = [
      '#ff0000', '#ff6600', '#ff9900', '#ffcc00', '#ffff00',
      '#00ff00', '#00ffff', '#0066ff', '#0000ff', '#9900ff', '#ff00ff'
    ].slice(0, 10); // Ensure exactly 10 colors

    // Main color grid (10 columns x 5 rows = 50 colors)
    const mainGrid = [];
    const hues = [0, 20, 40, 60, 90, 120, 180, 240, 270, 300]; // 10 hue values
    const saturations = [100, 85, 70, 55, 40]; // 5 saturation values
    const lightnesses = [50, 60, 70, 80, 90]; // 5 lightness values

    for (let row = 0; row < 5; row++) {
      for (let col = 0; col < 10; col++) {
        const hue = hues[col];
        const saturation = saturations[row];
        const lightness = lightnesses[row];
        const color = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
        mainGrid.push(color);
      }
    }

    return { grayscale, brightColors, mainGrid };
  };

  /**
   * Handle text color selection
   */
  const handleTextColorSelect = (color) => {
    setSelectedTextColor(color);
    document.execCommand('foreColor', false, color);
    bodyTextareaRef.current?.focus();
  };

  /**
   * Handle background color selection
   */
  const handleBgColorSelect = (color) => {
    setSelectedBgColor(color);
    document.execCommand('backColor', false, color);
    bodyTextareaRef.current?.focus();
  };

  /**
   * Handle text alignment
   */
  const handleAlignment = (alignment) => {
    setSelectedAlignment(alignment);
    if (alignment === 'center') {
      document.execCommand('justifyCenter', false, null);
    } else if (alignment === 'right') {
      document.execCommand('justifyRight', false, null);
    } else {
      document.execCommand('justifyLeft', false, null);
    }
    bodyTextareaRef.current?.focus();
    setShowAlignmentDropdown(false);
  };

  /**
   * Handle numbered list
   */
  const handleNumberedList = () => {
    document.execCommand('insertOrderedList', false, null);
    bodyTextareaRef.current?.focus();
  };

  /**
   * Handle bullet list
   */
  const handleBulletList = () => {
    document.execCommand('insertUnorderedList', false, null);
    bodyTextareaRef.current?.focus();
  };

  /**
   * Handle strikethrough
   */
  const handleStrikethrough = () => {
    document.execCommand('strikeThrough', false, null);
    bodyTextareaRef.current?.focus();
  };

  /**
   * Handle increase indent
   */
  const handleIncreaseIndent = () => {
    document.execCommand('indent', false, null);
    bodyTextareaRef.current?.focus();
  };

  /**
   * Handle decrease indent
   */
  const handleDecreaseIndent = () => {
    document.execCommand('outdent', false, null);
    bodyTextareaRef.current?.focus();
  };

  /**
   * Handle superscript
   */
  const handleSuperscript = () => {
    document.execCommand('superscript', false, null);
    bodyTextareaRef.current?.focus();
  };

  /**
   * Handle subscript
   */
  const handleSubscript = () => {
    document.execCommand('subscript', false, null);
    bodyTextareaRef.current?.focus();
  };

  // Load draft if provided or handle reply/forward from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const replyId = params.get('reply');
    const forwardId = params.get('forward');
    const replyUid = params.get('replyUid');
    const forwardUid = params.get('forwardUid');
    const toParam = params.get('to');

    if (draft) {
      setToRecipients(parseRecipientList(draft.to));
      setToInput('');
      setCc(draft.cc ? (Array.isArray(draft.cc) ? draft.cc.join(', ') : draft.cc) : '');
      setBcc(draft.bcc ? (Array.isArray(draft.bcc) ? draft.bcc.join(', ') : draft.bcc) : '');
      setShowCc(!!(draft.cc && (Array.isArray(draft.cc) ? draft.cc.length > 0 : draft.cc.trim())));
      setShowBcc(!!(draft.bcc && (Array.isArray(draft.bcc) ? draft.bcc.length > 0 : draft.bcc.trim())));
      setSubject(draft.subject || '');
      setBody(draft.body || '');
      setDraftId(draft._id);
      // The body editor is an uncontrolled contentEditable, so push the draft
      // content into the DOM once it has mounted.
      setTimeout(() => {
        const el = bodyTextareaRef.current;
        if (el) el.innerHTML = draft.body || '';
      }, 0);
    } else if (replyUid) {
      loadOriginalMail(replyUid, 'reply', true);
    } else if (forwardUid) {
      loadOriginalMail(forwardUid, 'forward', true);
    } else if (replyId) {
      loadOriginalMail(replyId, 'reply');
    } else if (forwardId) {
      loadOriginalMail(forwardId, 'forward');
    } else if (toParam) {
      setToRecipients(parseRecipientList(toParam));
      setToInput('');
    }
  }, [draft, location.search]);

  useEffect(() => {
    if (!groupId) {
      setGroupInfo(null);
      return;
    }

    const loadGroupInfo = async () => {
      try {
        const response = await axiosInstance.get('/api/groups');
        if (response.data.success) {
          const group = (response.data.data || []).find((item) => item._id === groupId);
          setGroupInfo(group || null);
        }
      } catch (error) {
        console.error('Error loading group info:', error);
      }
    };

    loadGroupInfo();
  }, [groupId]);

  const getRecipientValue = (field) => {
    if (field === 'cc') return cc;
    if (field === 'bcc') return bcc;
    return toInput;
  };

  const setRecipientValue = (field, value) => {
    if (field === 'cc') {
      setCc(value);
      return;
    }
    if (field === 'bcc') {
      setBcc(value);
      return;
    }
    setToInput(value);
  };

  const openRecipientHover = (email) => {
    if (recipientHoverTimeoutRef.current) {
      clearTimeout(recipientHoverTimeoutRef.current);
      recipientHoverTimeoutRef.current = null;
    }
    setHoveredRecipientEmail(email);
    ensureRecipientMeta(email);
  };

  const closeRecipientHover = () => {
    if (recipientHoverTimeoutRef.current) {
      clearTimeout(recipientHoverTimeoutRef.current);
    }
    recipientHoverTimeoutRef.current = setTimeout(() => {
      setHoveredRecipientEmail('');
      recipientHoverTimeoutRef.current = null;
    }, 180);
  };

  useEffect(() => () => {
    if (recipientHoverTimeoutRef.current) {
      clearTimeout(recipientHoverTimeoutRef.current);
    }
  }, []);

  const getRecipientDisplayUser = (email) => {
    const meta = recipientMeta[email] || {};
    return {
      email,
      username: meta.displayName || email.split('@')[0],
      displayName: meta.displayName || email.split('@')[0],
      profileImage: meta.avatar || meta.profileImage || '',
    };
  };

  const ensureRecipientMeta = async (email) => {
    const normalized = String(email || '').trim().toLowerCase();
    if (!normalized || recipientMeta[normalized]?.loaded) return;

    try {
      const response = await axiosInstance.get('/api/contacts/search', {
        params: { q: normalized },
      });
      const match = (response.data.data || []).find(
        (entry) => String(entry.email || '').toLowerCase() === normalized
      );
      const isInContactList = match?.source === 'manual';
      setRecipientMeta((prev) => ({
        ...prev,
        [normalized]: {
          displayName: match?.displayName || match?.username || normalized.split('@')[0],
          avatar: match?.avatar || match?.profileImage || '',
          contactId: isInContactList ? match?._id || '' : '',
          source: match?.source || '',
          isSaved: isInContactList,
          loaded: true,
        },
      }));
    } catch (error) {
      console.error('Error loading recipient contact info:', error);
      setRecipientMeta((prev) => ({
        ...prev,
        [normalized]: {
          displayName: normalized.split('@')[0],
          avatar: '',
          contactId: '',
          source: '',
          isSaved: false,
          loaded: true,
        },
      }));
    }
  };

  const handleAddRecipientContact = async (email) => {
    const normalized = String(email || '').trim().toLowerCase();
    if (!normalized) return;
    setSavingContactEmail(normalized);
    try {
      const meta = recipientMeta[normalized] || {};
      const response = await axiosInstance.post('/api/contacts', {
        email: normalized,
        displayName: meta.displayName || '',
      });
      if (response.data.success) {
        const saved = response.data.data;
        setRecipientMeta((prev) => ({
          ...prev,
          [normalized]: {
            displayName: saved.displayName || saved.username || normalized.split('@')[0],
            avatar: saved.avatar || saved.profileImage || '',
            contactId: saved._id,
            source: 'manual',
            isSaved: true,
            loaded: true,
          },
        }));
        setMessage({ type: 'success', text: 'Contact saved' });
        window.dispatchEvent(new CustomEvent('contactsUpdated'));
      } else {
        setMessage({ type: 'error', text: response.data.message || 'Could not save contact' });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Could not save contact',
      });
    } finally {
      setSavingContactEmail('');
    }
  };

  const handleEditRecipientContact = async (email, displayName) => {
    const normalized = String(email || '').trim().toLowerCase();
    const meta = recipientMeta[normalized] || {};
    const contactId = meta.contactId;
    if (!normalized || !contactId || meta.source !== 'manual') return;

    try {
      const response = await axiosInstance.patch(`/api/contacts/${contactId}`, {
        displayName,
      });
      if (response.data.success) {
        const updated = response.data.data;
        setRecipientMeta((prev) => ({
          ...prev,
          [normalized]: {
            ...prev[normalized],
            displayName: updated.displayName || updated.username || displayName,
            source: 'manual',
            isSaved: true,
            loaded: true,
          },
        }));
        setMessage({ type: 'success', text: 'Contact updated' });
        window.dispatchEvent(new CustomEvent('contactsUpdated'));
      } else {
        setMessage({ type: 'error', text: response.data.message || 'Could not update contact' });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Could not update contact',
      });
    }
  };

  const addToRecipient = (rawEmail, { showErrors = true, meta = null } = {}) => {
    const email = String(rawEmail || '').trim().toLowerCase();
    if (!email) return false;
    if (!RECIPIENT_EMAIL_REGEX.test(email)) {
      if (showErrors) {
        setMessage({ type: 'error', text: `"${rawEmail.trim()}" is not a valid email address` });
      }
      return false;
    }
    if (email === currentUser?.email?.toLowerCase()) {
      if (showErrors) {
        setMessage({ type: 'error', text: 'You cannot add yourself as a recipient' });
      }
      return false;
    }
    if (toRecipients.includes(email)) {
      setToInput('');
      return false;
    }
    setToRecipients((prev) => [...prev, email]);
    if (meta) {
      const isInContactList = meta.source === 'manual';
      setRecipientMeta((prev) => ({
        ...prev,
        [email]: {
          displayName: meta.displayName || meta.username || email.split('@')[0],
          avatar: meta.avatar || meta.profileImage || '',
          contactId: isInContactList ? (meta._id || meta.contactId || '') : '',
          source: meta.source || '',
          isSaved: isInContactList,
          loaded: true,
        },
      }));
    }
    setToInput('');
    setMessage({ type: '', text: '' });
    return true;
  };

  const removeToRecipient = (email) => {
    setToRecipients((prev) => prev.filter((entry) => entry !== email));
    setHoveredRecipientEmail((current) => (current === email ? '' : current));
  };

  const getGroupMemberEmails = (group) => (
    (group?.members || [])
      .map((member) => String(member?.email || '').trim().toLowerCase())
      .filter((email) => (
        email &&
        email !== currentUser?.email?.toLowerCase() &&
        RECIPIENT_EMAIL_REGEX.test(email)
      ))
  );

  const getAllToRecipients = () => {
    const pending = toInput.trim().toLowerCase();
    if (!pending) return [...toRecipients];
    if (toRecipients.includes(pending)) return [...toRecipients];
    return [...toRecipients, pending];
  };

  const handleRecipientKeyDown = (field, event) => {
    const hasSuggestions =
      recipientSuggestions.length > 0 && activeRecipientField === field;

    if (event.key === 'ArrowDown' && hasSuggestions) {
      event.preventDefault();
      setSelectedSuggestionIndex((prev) =>
        prev < recipientSuggestions.length - 1 ? prev + 1 : 0
      );
      return true;
    }

    if (event.key === 'ArrowUp' && hasSuggestions) {
      event.preventDefault();
      setSelectedSuggestionIndex((prev) =>
        prev > 0 ? prev - 1 : recipientSuggestions.length - 1
      );
      return true;
    }

    if (
      (event.key === 'Enter' || event.key === 'Tab') &&
      hasSuggestions &&
      selectedSuggestionIndex >= 0
    ) {
      event.preventDefault();
      applyRecipientSuggestion(field, recipientSuggestions[selectedSuggestionIndex]);
      setSelectedSuggestionIndex(-1);
      return true;
    }

    if (event.key === 'Escape' && hasSuggestions) {
      event.preventDefault();
      setRecipientSuggestions([]);
      setSelectedSuggestionIndex(-1);
      return true;
    }

    return false;
  };

  const handleToKeyDown = (event) => {
    if (handleRecipientKeyDown('to', event)) return;

    if ((event.key === 'Enter' || event.key === ',') && toInput.trim()) {
      if (selectedSuggestionIndex >= 0) return;
      event.preventDefault();
      addToRecipient(toInput);
      return;
    }
    if (event.key === 'Backspace' && !toInput && toRecipients.length > 0) {
      setToRecipients((prev) => prev.slice(0, -1));
    }
  };

  useEffect(() => {
    const suggestionField = activeRecipientField || (toInput.trim() ? 'to' : '');
    const value = suggestionField && !isGroupCompose
      ? suggestionField === 'cc'
        ? cc
        : suggestionField === 'bcc'
          ? bcc
          : toInput
      : '';
    const query = value.split(',').pop().trim();
    if (!query) {
      setRecipientSuggestions([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        const contactsRequest = axiosInstance.get('/api/contacts/search', {
          params: { q: query },
        });
        const groupsRequest = suggestionField === 'to'
          ? axiosInstance.get('/api/groups')
          : Promise.resolve(null);
        const [contactsResponse, groupsResponse] = await Promise.all([
          contactsRequest,
          groupsRequest,
        ]);

        const suggestions = [];
        if (contactsResponse.data.success) {
          suggestions.push(
            ...(contactsResponse.data.data || []).map(mapContactToSuggestion)
          );
        }

        if (groupsResponse?.data?.success) {
          suggestions.push(
            ...filterGroupsByQuery(groupsResponse.data.data, query).map(mapGroupToSuggestion)
          );
        }

        setRecipientSuggestions(suggestions.slice(0, CONTACT_SEARCH_LIMIT));
      } catch (error) {
        console.error('Error fetching recipient suggestions:', error);
        setRecipientSuggestions([]);
      }
    }, CONTACT_SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [activeRecipientField, toInput, cc, bcc, isGroupCompose]);

  const applyRecipientSuggestion = (field, suggestion) => {
    if (suggestion?.suggestionType === 'group') {
      const memberEmails = getGroupMemberEmails(suggestion);
      if (!memberEmails.length) {
        setMessage({
          type: 'error',
          text: `"${suggestion.name}" does not have any other valid recipients`,
        });
        setToInput('');
        setRecipientSuggestions([]);
        setActiveRecipientField('');
        return;
      }

      setToRecipients((prev) => [...new Set([...prev, ...memberEmails])]);
      setToInput('');
      setRecipientSuggestions([]);
      setActiveRecipientField('');
      setMessage({ type: '', text: '' });
      return;
    }

    const email = suggestion?.email || suggestion;
    if (field === 'to') {
      addToRecipient(email, {
        showErrors: false,
        meta: suggestion?.suggestionType === 'contact' ? suggestion : null,
      });
      setRecipientSuggestions([]);
      return;
    }
    const value = getRecipientValue(field);
    const parts = value.split(',');
    parts[parts.length - 1] = ` ${email}`;
    const nextValue = parts.map((part) => part.trim()).filter(Boolean).join(', ');
    setRecipientValue(field, nextValue);
    setRecipientSuggestions([]);
  };

  const shouldShowRecipientSuggestions = (field) => {
    if (recipientSuggestions.length === 0) return false;
    if (activeRecipientField !== field) return false;
    if (field === 'to') return toInput.trim().length > 0;
    return true;
  };

  const handleSuggestionPointerDown = (field, suggestion) => (event) => {
    event.preventDefault();
    selectingSuggestionRef.current = true;
    applyRecipientSuggestion(field, suggestion);
    setSelectedSuggestionIndex(-1);
    setTimeout(() => {
      selectingSuggestionRef.current = false;
    }, 0);
  };

  const renderRecipientSuggestions = (field) => {
    if (!shouldShowRecipientSuggestions(field)) return null;
    return (
      <div className={`recipient-suggestions${isMobileView ? ' recipient-suggestions-mobile' : ''}`}>
        {recipientSuggestions.map((suggestion, index) =>
          renderSuggestionResult(field, suggestion, index)
        )}
      </div>
    );
  };

  /**
   * Load original mail for reply or forward
   */
  const loadOriginalMail = async (mailId, type, isServerMail = false) => {
    if (!mailId || mailId === 'undefined' || mailId === 'null') {
      setMessage({ type: 'error', text: 'Original mail is not available' });
      return;
    }

    setLoadingOriginalMail(true);
    try {
      const endpoint = isServerMail
        ? `/api/mail/server/message/${encodeURIComponent(mailId)}`
        : `/api/mail/${encodeURIComponent(mailId)}`;
      const response = await axiosInstance.get(endpoint);
      if (response.data.success) {
        const originalMail = response.data.data;
        const originalFrom = originalMail.from || '';
        const originalSubject = originalMail.subject || '';
        const originalBody = originalMail.body || originalMail.text || '';
        const originalDateValue = originalMail.createdAt || originalMail.date;
        const originalDate = originalDateValue ? new Date(originalDateValue).toLocaleString() : '';
        
        if (type === 'reply') {
          setToRecipients(parseRecipientList(originalFrom));
          setToInput('');
          setSubject(originalSubject.startsWith('Re:') ? originalSubject : `Re: ${originalSubject}`);
          const replyHtml = `<br><br>On ${originalDate}, ${originalFrom} wrote:<br>${originalBody}`;
          setBody(replyHtml);
          setTimeout(() => {
            const el = bodyTextareaRef.current;
            if (el) {
              el.innerHTML = replyHtml;
              placeCaretAtComposeStart(el);
            }
          }, 0);
          if (originalMail.cc && originalMail.cc.length > 0) {
            setCc(Array.isArray(originalMail.cc) ? originalMail.cc.join(', ') : originalMail.cc);
            setShowCc(true);
          }
        } else if (type === 'forward') {
          setToRecipients([]);
          setToInput('');
          setAttachedFiles([]);
          setSubject(originalSubject.startsWith('Fwd:') ? originalSubject : `Fwd: ${originalSubject}`);
          const forwardHtml = buildGmailForwardBodyHtml(originalMail);
          setBody(forwardHtml);
          setTimeout(() => {
            const el = bodyTextareaRef.current;
            if (el) {
              el.innerHTML = forwardHtml;
              placeCaretAtComposeStart(el);
            }
          }, 0);

          const {
            files: forwardedFiles,
            failed: failedAttachments,
            total: attachmentTotal,
          } = await loadForwardAttachments(originalMail, mailId, isServerMail);

          if (forwardedFiles.length > 0) {
            setAttachedFiles(forwardedFiles);
          }

          if (attachmentTotal > 0 && failedAttachments > 0) {
            setMessage({
              type: 'error',
              text:
                forwardedFiles.length > 0
                  ? `Forwarded with ${forwardedFiles.length}/${attachmentTotal} file(s). ${failedAttachments} could not be attached — add them manually if needed.`
                  : 'Could not attach original files. Please attach them manually.',
            });
          }
        }
      }
    } catch (error) {
      console.error('Error loading original mail:', error);
      setMessage({ type: 'error', text: 'Failed to load original mail' });
    } finally {
      setLoadingOriginalMail(false);
    }
  };

  /**
   * Autosave draft functionality
   * Don't autosave if currently sending
   */
  useEffect(() => {
    // Don't autosave while sending or after discard
    if (isSending || discardRequestedRef.current) {
      return;
    }

    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }

    if (isGroupCompose) {
      return;
    }

    // Ignore an auto-inserted signature when deciding if the compose is empty,
    // so a fresh compose with only a signature doesn't create a junk draft.
    const bodyWithoutSignature = String(body || '').replace(
      /<div[^>]*data-atozas-signature="1"[^>]*>[\s\S]*?<\/div>/g,
      ''
    );
    if (
      !toRecipients.length &&
      !toInput.trim() &&
      !subject.trim() &&
      !getBodyTextContent(bodyWithoutSignature) &&
      !cc.trim() &&
      !bcc.trim()
    ) {
      return;
    }

    autosaveTimerRef.current = setTimeout(() => {
      if (!discardRequestedRef.current) {
        saveDraftRef.current();
      }
    }, 3000);

    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }
    };
  }, [toRecipients, toInput, cc, bcc, subject, body, isSending, isGroupCompose]);

  const saveDraft = async () => {
    if (discardRequestedRef.current) return;
    try {
      setDraftStatus('saving');
      const response = await axiosInstance.post('/api/mail/draft', {
        draftId,
        to: formatRecipientList(getAllToRecipients()),
        cc: cc.trim(),
        bcc: bcc.trim(),
        subject: subject.trim(),
        body: body.trim(),
      });

      if (discardRequestedRef.current) {
        // Draft was discarded while save was in flight — delete it.
        const savedId = response.data?.data?._id;
        if (savedId) {
          try {
            await axiosInstance.delete(`/api/mail/draft/${savedId}`);
          } catch (deleteError) {
            console.error('Error deleting draft after discard:', deleteError);
          }
        }
        return;
      }

      if (response.data.success) {
        setDraftId(response.data.data._id);
        setDraftStatus('saved');
        setTimeout(() => {
          setDraftStatus('');
        }, 2000);
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      setDraftStatus('');
    }
  };

  const saveDraftRef = useRef(saveDraft);
  saveDraftRef.current = saveDraft;

  const navigateAwayFromCompose = () => {
    if (location.pathname === '/compose') {
      navigate('/inbox');
    } else {
      navigate(-1);
    }
  };

  /**
   * Close compose: save as draft when subject/content/recipients exist.
   * Delete/discard button intentionally does not use this path.
   */
  const handleComposeClose = async () => {
    if (discardRequestedRef.current || isSending) {
      navigateAwayFromCompose();
      return;
    }

    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }

    if (!isGroupCompose && !isComposeEmpty) {
      await saveDraftRef.current();
    }

    if (onClearDraft) {
      onClearDraft();
    }

    navigateAwayFromCompose();
  };

  /**
   * Handle keyboard shortcuts: Ctrl + Enter to send mail, Ctrl+B/I/U for formatting
   */
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        if (isBodyFocused) {
          e.preventDefault();
          handleSend();
        }
      } else if ((e.ctrlKey || e.metaKey) && isBodyFocused) {
        if (e.key === 'b' || e.key === 'B') {
          e.preventDefault();
          handleBold();
        } else if (e.key === 'i' || e.key === 'I') {
          e.preventDefault();
          handleItalic();
        } else if (e.key === 'u' || e.key === 'U') {
          e.preventDefault();
          handleUnderline();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isBodyFocused, toRecipients, toInput, cc, bcc, subject, body, currentUser, draftId]);

  /**
   * Handle sending the mail
   */
  const UNDO_SECONDS = 7;

  const cleanupUndo = () => {
    if (undoTimerRef.current) {
      clearTimeout(undoTimerRef.current);
      undoTimerRef.current = null;
    }
  };

  // Shows a Gmail-style "Sending… / Cancel" toast for a few seconds before the
  // mail is actually sent. Resolves true if cancelled (undo), false to proceed.
  const waitUndoWindow = () =>
    new Promise((resolve) => {
      suppressSentToastRef.current = false;
      setSendToast({ status: 'sending', text: 'Sending…' });
      undoResolveRef.current = (undone) => {
        undoResolveRef.current = null;
        cleanupUndo();
        resolve(undone);
      };
      undoTimerRef.current = setTimeout(() => {
        if (undoResolveRef.current) undoResolveRef.current(false);
      }, UNDO_SECONDS * 1000);
    });

  // "Cancel" — stop the send and keep the draft open.
  const handleUndoSend = () => {
    if (undoResolveRef.current) undoResolveRef.current(true);
    setSendToast(null);
  };

  // "✕" on the Sending toast — dismiss the toast, let the mail send silently.
  const handleDismissSendToast = () => {
    suppressSentToastRef.current = true;
    setSendToast(null);
    if (sentToastTimerRef.current) {
      clearTimeout(sentToastTimerRef.current);
      sentToastTimerRef.current = null;
    }
  };

  useEffect(
    () => () => {
      cleanupUndo();
      if (sentToastTimerRef.current) clearTimeout(sentToastTimerRef.current);
    },
    []
  );

  // ---- Signature ----
  const signatureText = currentUser?.signature || '';

  const buildSignatureHtml = () => {
    if (!signatureText.trim()) return '';
    const escaped = signatureText
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br>');
    return `<div class="atozas-signature" data-atozas-signature="1">--<br>${escaped}</div>`;
  };

  const stripSignature = (html) =>
    String(html || '').replace(/<div[^>]*data-atozas-signature="1"[^>]*>[\s\S]*?<\/div>/g, '');

  const syncBodyDom = (html) => {
    setBody(html);
    const el = bodyTextareaRef.current;
    if (el) el.innerHTML = html;
  };

  const applySignature = () => {
    const sig = buildSignatureHtml();
    if (!sig) return;
    const current = stripSignature(bodyTextareaRef.current?.innerHTML ?? body).replace(/(<br>\s*)+$/, '');
    const sep = current.trim() ? '<br><br>' : '';
    syncBodyDom(`${current}${sep}${sig}`);
    setSelectedSignature('my');
    setShowSignatureDropdown(false);
  };

  const clearSignature = () => {
    const current = stripSignature(bodyTextareaRef.current?.innerHTML ?? body).replace(/(<br>\s*)+$/, '');
    syncBodyDom(current);
    setSelectedSignature('no-signature');
    setShowSignatureDropdown(false);
  };

  // Auto-insert signature for a fresh compose (not reply/forward/draft/group)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isFresh =
      !draft &&
      !params.get('reply') &&
      !params.get('forward') &&
      !params.get('replyUid') &&
      !params.get('forwardUid') &&
      !params.get('to') &&
      !isGroupCompose;
    if (!isFresh || !signatureText.trim()) return undefined;
    const timer = setTimeout(() => {
      const el = bodyTextareaRef.current;
      const existing = (el?.innerHTML ?? body) || '';
      if (/data-atozas-signature/.test(existing)) return;
      const html = `${existing}<br><br>${buildSignatureHtml()}`;
      setBody(html);
      if (el) el.innerHTML = html;
      setSelectedSignature('my');
    }, 50);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSend = async () => {
    await handleSendWithSchedule(null);
  };

  const handleDiscard = () => {
    setShowDiscardConfirm(true);
  };

  const confirmDiscard = async () => {
    discardRequestedRef.current = true;
    setShowDiscardConfirm(false);

    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }

    const idToDelete = draftId;
    setDraftId(null);
    setDraftStatus('');
    if (onClearDraft) {
      onClearDraft();
    }

    if (idToDelete) {
      try {
        await axiosInstance.delete(`/api/mail/draft/${idToDelete}`);
      } catch (error) {
        console.error('Error deleting draft:', error);
      }
    }

    if (location.pathname === '/compose') {
      navigate('/inbox');
    } else {
      navigate(-1);
    }
  };

  const handleMinimize = () => {
    setWindowSize('minimized');
  };

  const handleExpand = () => {
    setWindowSize(windowSize === 'expanded' ? 'normal' : 'expanded');
  };

  const handleScheduleSend = () => {
    setShowScheduleModal(true);
    setShowSendOptions(false);
  };

  const buildScheduledDateTime = (dateStr, timeStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const [hours, minutes] = timeStr.split(':').map(Number);
    return new Date(year, month - 1, day, hours, minutes || 0, 0, 0);
  };

  const handleScheduleConfirm = async () => {
    if (!scheduleDate || !scheduleTime) {
      setMessage({ type: 'error', text: 'Please select both date and time' });
      return;
    }

    const scheduledDateTime = buildScheduledDateTime(scheduleDate, scheduleTime);
    const now = new Date();

    if (scheduledDateTime <= now) {
      setMessage({ type: 'error', text: 'Please select a future date and time' });
      return;
    }

    setShowScheduleModal(false);
    await handleSendWithSchedule(scheduledDateTime);
  };

  const handleSendWithSchedule = async (scheduledFor = null) => {
    if (isGroupCompose && scheduledFor) {
      setMessage({ type: 'error', text: 'Scheduled send is not supported for group mail yet' });
      return;
    }

    if (!isGroupCompose && toRecipients.length === 0 && !toInput.trim()) {
      setMessage({ type: 'error', text: 'Please add at least one recipient' });
      return;
    }
    if (!subject.trim()) {
      setMessage({ type: 'error', text: 'Please enter subject' });
      return;
    }
    if (!getBodyTextContent(body)) {
      setMessage({ type: 'error', text: 'Please enter message body' });
      return;
    }

    const emailRegex = RECIPIENT_EMAIL_REGEX;
    if (!isGroupCompose) {
      const allToEmails = getAllToRecipients();
      if (!allToEmails.length) {
        setMessage({ type: 'error', text: 'Please add at least one recipient' });
        return;
      }
      for (const email of allToEmails) {
        if (!emailRegex.test(email)) {
          setMessage({
            type: 'error',
            text: `To email "${email}" is not a valid email address`,
          });
          return;
        }
        if (email === currentUser?.email?.toLowerCase()) {
          setMessage({ type: 'error', text: 'You cannot send mail to yourself' });
          return;
        }
      }
    }

    if (cc.trim()) {
      const ccEmails = cc.split(',').map(e => e.trim()).filter(e => e);
      for (const email of ccEmails) {
        if (!emailRegex.test(email)) {
          setMessage({ 
            type: 'error', 
            text: `CC email "${email}" is not a valid email address` 
          });
          return;
        }
      }
    }

    if (bcc.trim()) {
      const bccEmails = bcc.split(',').map(e => e.trim()).filter(e => e);
      for (const email of bccEmails) {
        if (!emailRegex.test(email)) {
          setMessage({ 
            type: 'error', 
            text: `BCC email "${email}" is not a valid email address` 
          });
          return;
        }
      }
    }

    // Clear autosave timer to prevent saving as draft while sending
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }

    // Undo send window (immediate sends only, not scheduled)
    if (!scheduledFor) {
      setShowSendOptions(false);
      const undone = await waitUndoWindow();
      if (undone) {
        // Cancelled — toast already hidden, keep the draft open.
        return;
      }
    }

    setIsSending(true);
    setMessage({ type: '', text: '' });

    const sendBody = buildSendBodyPayload(body);
    const toValue = isGroupCompose ? '' : formatRecipientList(getAllToRecipients());

    try {
      // Create FormData if there are attachments, otherwise use JSON
      let response;
      if (attachedFiles.length > 0) {
        console.log(`📎 Sending ${attachedFiles.length} file(s) with email`);
        const formData = new FormData();
        if (isGroupCompose) {
          formData.append('groupId', groupId);
        } else {
          formData.append('to', toValue);
          if (cc.trim()) formData.append('cc', cc.trim());
          if (bcc.trim()) formData.append('bcc', bcc.trim());
        }
        formData.append('subject', subject.trim());
        formData.append('body', sendBody.body);
        if (sendBody.html) formData.append('html', sendBody.html);
        if (draftId) formData.append('draftId', draftId);
        if (scheduledFor) formData.append('scheduledFor', scheduledFor.toISOString());
        
        // Append all files
        attachedFiles.forEach((file, index) => {
          console.log(`   Appending file ${index + 1}: ${file.name} (${(file.size / 1024).toFixed(2)} KB)`);
          formData.append('attachments', file);
        });

        // Don't set Content-Type header - axios will set it automatically with boundary
        response = await axiosInstance.post('/api/mail/send', formData);
      } else {
        response = await axiosInstance.post('/api/mail/send', {
          to: isGroupCompose ? undefined : toValue,
          cc: !isGroupCompose && cc.trim() ? cc.trim() : undefined,
          bcc: !isGroupCompose && bcc.trim() ? bcc.trim() : undefined,
          groupId: isGroupCompose ? groupId : undefined,
          subject: subject.trim(),
          body: sendBody.body,
          html: sendBody.html,
          draftId: draftId,
          scheduledFor: scheduledFor ? scheduledFor.toISOString() : undefined,
        });
      }

      if (response.data.success) {
        // Clear autosave timer to prevent saving after successful send
        if (autosaveTimerRef.current) {
          clearTimeout(autosaveTimerRef.current);
          autosaveTimerRef.current = null;
        }

        if (!suppressSentToastRef.current) {
          setSendToast({
            status: 'sent',
            text: scheduledFor ? 'Message scheduled' : 'Message sent',
          });
        }
        setToRecipients([]);
        setToInput('');
        setCc('');
        setBcc('');
        setShowCc(false);
        setShowBcc(false);
        setSubject('');
        setBody('');
        setDraftId(null);
        setScheduleDate('');
        setScheduleTime('');
        setAttachedFiles([]);
        
        if (onClearDraft) {
          onClearDraft();
        }
        
        setTimeout(() => {
          setMessage({ type: '', text: '' });
          if (scheduledFor) {
            navigate('/scheduled');
          } else if (isGroupCompose) {
            navigate(`/groups/${groupId}`);
          } else {
            navigate('/inbox');
          }
        }, 1500);
      } else {
        setSendToast(null);
        setMessage({ 
          type: 'error', 
          text: response.data.message || 'Failed to send mail' 
        });
      }
    } catch (error) {
      console.error('Error sending mail:', error);
      setSendToast(null);
      setMessage({ 
        type: 'error', 
        text:
          error.response?.data?.message ||
          error.response?.data?.error ||
          'Error sending mail. Please try again.' 
      });
    } finally {
      setIsSending(false);
    }
  };

  // Set default date and time for schedule picker
  useEffect(() => {
    if (showScheduleModal && !scheduleDate) {
      const defaultTime = new Date();
      defaultTime.setMinutes(defaultTime.getMinutes() + 30);
      const y = defaultTime.getFullYear();
      const m = String(defaultTime.getMonth() + 1).padStart(2, '0');
      const d = String(defaultTime.getDate()).padStart(2, '0');
      setScheduleDate(`${y}-${m}-${d}`);
      const hours = String(defaultTime.getHours()).padStart(2, '0');
      const minutes = String(defaultTime.getMinutes()).padStart(2, '0');
      setScheduleTime(`${hours}:${minutes}`);
    }
  }, [showScheduleModal, scheduleDate]);

  // Close send options when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSendOptions && !event.target.closest('.send-options-container')) {
        setShowSendOptions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSendOptions]);

  // Close emoji picker when clicking outside
  useEffect(() => {
    if (!showEmojiPicker) return;
    
    const handleClickOutside = (event) => {
      // Check if click is inside emoji picker or on emoji button
      const emojiPickerWrapper = event.target.closest('.emoji-picker-wrapper');
      const emojiButton = event.target.closest('button[title="Add emoji"]');
      const emojiPickerElement = event.target.closest('aside.EmojiPickerReact');
      
      // Close if clicking outside emoji picker and not on emoji button
      if (!emojiPickerWrapper && !emojiButton && !emojiPickerElement) {
        setShowEmojiPicker(false);
      }
    };
    
    // Add event listener after a small delay to avoid immediate close
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);
    
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);

  // Close color picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showColorPicker && 
          !event.target.closest('.color-picker-dialog-below-subject') &&
          !event.target.closest('.compose-subject-section') &&
          !event.target.closest('.color-picker-container')) {
        setShowColorPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showColorPicker]);

  // Close alignment dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showAlignmentDropdown && !event.target.closest('.alignment-dropdown-container')) {
        setShowAlignmentDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAlignmentDropdown]);

  // Close formatting more options dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showFormattingMoreOptions && !event.target.closest('.formatting-more-options-container')) {
        setShowFormattingMoreOptions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFormattingMoreOptions]);

  // Close more options dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showMoreOptionsDropdown &&
        !event.target.closest('.more-options-dropdown-container') &&
        !event.target.closest('.compose-mobile-more-menu') &&
        !event.target.closest('.compose-mobile-menu-backdrop')
      ) {
        setShowMoreOptionsDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showMoreOptionsDropdown]);

  /**
   * Handle file attachment
   */
  const handleAttachFiles = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setAttachedFiles(prev => [...prev, ...files]);
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (loadingOriginalMail) {
    return (
      <div className="compose-mail-container">
        <div className="loading-compose">Loading...</div>
      </div>
    );
  }

  if (windowSize === 'minimized') {
    return (
      <div className="compose-mail-container compose-minimized">
        <div className="compose-header-gmail">
          <div className="compose-header-top">
            <div className="compose-title-row">
              {composeHeaderTitle === 'Forward' && (
                <span className="compose-title-mode-icon" title="Forward" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8-8-8z" />
                  </svg>
                </span>
              )}
              {composeHeaderTitle === 'Reply' && (
                <span className="compose-title-mode-icon" title="Reply" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z" />
                  </svg>
                </span>
              )}
              <h2
                className={`compose-title${composeHeaderTitle !== 'New Message' ? ' compose-title-mode' : ''}`}
              >
                {composeHeaderTitle}
              </h2>
            </div>
            <div className="compose-window-controls">
              <button className="compose-expand-btn" onClick={handleExpand} title="Expand">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7 7h10v10H7V7zm2 2v6h6V9H9z"/>
                </svg>
              </button>
              <button className="compose-close-btn" onClick={handleComposeClose} title="Close">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`compose-mail-container ${windowSize === 'expanded' ? 'compose-expanded' : ''}${isMobileView ? ' compose-mobile' : ''}`}>
      {sendToast && (
        <div className="send-toast" role="status">
          <span className="send-toast-text">{sendToast.text}</span>
          {sendToast.status === 'sending' && (
            <button type="button" className="send-toast-action" onClick={handleUndoSend}>
              Cancel
            </button>
          )}
          <button
            type="button"
            className="send-toast-close"
            aria-label="Dismiss"
            onClick={sendToast.status === 'sending' ? handleDismissSendToast : () => setSendToast(null)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>
      )}
      <div className="compose-header-gmail">
        {isMobileView ? (
          <div className="compose-mobile-header">
            <button type="button" className="compose-mobile-back" onClick={handleComposeClose} aria-label="Back">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
              </svg>
            </button>
            <div className="compose-mobile-header-actions">
              <button
                type="button"
                className="compose-mobile-action-btn"
                title="Attach files"
                onClick={handleAttachFiles}
                disabled={isSending}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z" />
                </svg>
              </button>
              <button
                type="button"
                className="compose-mobile-send-btn"
                title="Send"
                onClick={handleSend}
                disabled={isSending || (sendToast && sendToast.status === 'sending')}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                </svg>
              </button>
              <div className="more-options-dropdown-container">
                <button
                  type="button"
                  className={`compose-mobile-action-btn ${showMoreOptionsDropdown ? 'active' : ''}`}
                  title="More options"
                  onClick={() => setShowMoreOptionsDropdown(!showMoreOptionsDropdown)}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="5" r="2" />
                    <circle cx="12" cy="12" r="2" />
                    <circle cx="12" cy="19" r="2" />
                  </svg>
                </button>
                {showMoreOptionsDropdown && renderComposeMoreMenu()}
              </div>
            </div>
          </div>
        ) : (
        <div className="compose-header-top">
          <div className="compose-title-row">
            {composeHeaderTitle === 'Forward' && (
              <span className="compose-title-mode-icon" title="Forward" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8-8-8z" />
                </svg>
              </span>
            )}
            {composeHeaderTitle === 'Reply' && (
              <span className="compose-title-mode-icon" title="Reply" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z" />
                </svg>
              </span>
            )}
            <h2
              className={`compose-title${composeHeaderTitle !== 'New Message' ? ' compose-title-mode' : ''}`}
            >
              {composeHeaderTitle}
            </h2>
          </div>
          <div className="compose-window-controls">
            <button className="compose-minimize-btn" onClick={handleMinimize} title="Minimize">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 13H5v-2h14v2z"/>
              </svg>
            </button>
            <button className="compose-expand-btn" onClick={handleExpand} title={windowSize === 'expanded' ? 'Restore' : 'Expand'}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                {windowSize === 'expanded' ? (
                  <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/>
                ) : (
                  <path d="M7 7h10v10H7V7zm2 2v6h6V9H9z"/>
                )}
              </svg>
            </button>
            <button className="compose-close-btn" onClick={handleComposeClose} title="Close">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>
          </div>
        </div>
        )}
      </div>

      <div className="compose-form-gmail">
        {isMobileView && (
          <div className="compose-from-section">
            <span className="compose-label">From</span>
            <span className="compose-from-value">{currentUser?.email || ''}</span>
            <button type="button" className="compose-field-chevron" aria-label="From account" disabled>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7 10l5 5 5-5z" />
              </svg>
            </button>
          </div>
        )}
        <div className={`compose-to-section${isMobileView ? ' compose-mobile-to' : ''}`}>
          <span className="compose-label">To</span>
          {isGroupCompose ? (
            <div className="compose-group-recipient">
              <span>{groupInfo?.name || 'Group'}</span>
              <small>{groupInfo?.members?.length || 0} members</small>
            </div>
          ) : (
            <div className="compose-recipient-input-wrap">
              {toRecipients.map((email) => {
                const recipientUser = getRecipientDisplayUser(email);
                const meta = recipientMeta[email] || {};
                return (
                  <span
                    className={`compose-recipient-hover-wrap${hoveredRecipientEmail === email ? ' is-open' : ''}`}
                    key={email}
                    onMouseEnter={() => openRecipientHover(email)}
                    onMouseLeave={closeRecipientHover}
                    onFocus={() => openRecipientHover(email)}
                  >
                    <span className="compose-recipient-chip">
                      <UserAvatar user={recipientUser} size="sm" className="compose-recipient-chip-avatar" />
                      <span className="compose-recipient-chip-label">{recipientUser.username}</span>
                      <button
                        type="button"
                        onClick={() => removeToRecipient(email)}
                        disabled={isSending}
                        aria-label={`Remove ${email}`}
                      >
                        ×
                      </button>
                    </span>
                    <ContactHoverCard
                      contact={recipientUser}
                      contactId={meta.contactId || ''}
                      isSaved={Boolean(meta.isSaved)}
                      isSaving={savingContactEmail === email}
                      onAddContact={() => handleAddRecipientContact(email)}
                      onEditContact={(displayName) => handleEditRecipientContact(email, displayName)}
                      onMouseEnter={() => openRecipientHover(email)}
                      onMouseLeave={closeRecipientHover}
                    />
                  </span>
                );
              })}
              <input
                ref={toRecipientInputRef}
                type="text"
                value={toInput}
                onChange={(e) => {
                  setToInput(e.target.value);
                  setActiveRecipientField('to');
                }}
                onKeyDown={handleToKeyDown}
                onFocus={() => setActiveRecipientField('to')}
                onBlur={() => {
                  const pendingInput = toInput;
                  const blurDelay = isTouchDevice() ? 500 : isMobileView ? 400 : 120;
                  setTimeout(() => {
                    if (selectingSuggestionRef.current) return;
                    const hasPendingSuggestions = recipientSuggestionsRef.current.length > 0;
                    if ((isMobileView || isTouchDevice()) && pendingInput.trim() && hasPendingSuggestions) {
                      setActiveRecipientField('to');
                      return;
                    }
                    if ((isMobileView || isTouchDevice()) && pendingInput.trim() && !RECIPIENT_EMAIL_REGEX.test(pendingInput.trim())) {
                      setActiveRecipientField('to');
                      return;
                    }
                    if (pendingInput.trim()) {
                      addToRecipient(pendingInput, { showErrors: false });
                    }
                    setActiveRecipientField('');
                  }, blurDelay);
                }}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                inputMode="email"
                placeholder={isMobileView ? '' : (toRecipients.length ? 'Add another recipient' : 'Recipients')}
                className="compose-input compose-recipient-inline-input"
                disabled={isSending}
              />
              {!isMobileView && renderRecipientSuggestions('to')}
            </div>
          )}
          {isMobileView && !isGroupCompose && (
            <button
              type="button"
              className="compose-field-chevron"
              aria-label="Show Cc and Bcc"
              onClick={() => {
                setShowMobileRecipientDetails((open) => {
                  const next = !open;
                  if (next && !showCc) setShowCc(true);
                  return next;
                });
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7 10l5 5 5-5z" />
              </svg>
            </button>
          )}
          {!isGroupCompose && (!isMobileView || showMobileRecipientDetails) && (
            <div className="cc-bcc-text">
              {!showCc && (
                <button
                  type="button"
                  className="cc-bcc-link"
                  onClick={() => setShowCc(true)}
                  disabled={isSending}
                >
                  Cc
                </button>
              )}
              {!showBcc && (
                <button
                  type="button"
                  className="cc-bcc-link"
                  onClick={() => setShowBcc(true)}
                  disabled={isSending}
                >
                  Bcc
                </button>
              )}
            </div>
          )}
        </div>

        {isGroupCompose ? (
          renderGroupComposeSuggestions()
        ) : (
          renderToSuggestionsPanel()
        )}

        {!isGroupCompose && showCc && (!isMobileView || showMobileRecipientDetails) && (
          <div className="compose-to-section">
            <span className="compose-label">Cc</span>
            <input
              type="text"
              value={cc}
              onChange={(e) => {
                setCc(e.target.value);
                setActiveRecipientField('cc');
              }}
              onFocus={() => setActiveRecipientField('cc')}
              onKeyDown={(event) => handleRecipientKeyDown('cc', event)}
              onBlur={() => setTimeout(() => setActiveRecipientField(''), 120)}
              autoComplete="off"
              placeholder="Cc"
              className="compose-input"
              disabled={isSending}
            />
            {renderRecipientSuggestions('cc')}
            <button
              type="button"
              className="cc-bcc-remove-btn"
              onClick={() => {
                setShowCc(false);
                setCc('');
              }}
              disabled={isSending}
              title="Remove Cc"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>
          </div>
        )}

        {!isGroupCompose && showBcc && (!isMobileView || showMobileRecipientDetails) && (
          <div className="compose-to-section">
            <span className="compose-label">Bcc</span>
            <input
              type="text"
              value={bcc}
              onChange={(e) => {
                setBcc(e.target.value);
                setActiveRecipientField('bcc');
              }}
              onFocus={() => setActiveRecipientField('bcc')}
              onKeyDown={(event) => handleRecipientKeyDown('bcc', event)}
              onBlur={() => setTimeout(() => setActiveRecipientField(''), 120)}
              autoComplete="off"
              placeholder="Bcc"
              className="compose-input"
              disabled={isSending}
            />
            {renderRecipientSuggestions('bcc')}
            <button
              type="button"
              className="cc-bcc-remove-btn"
              onClick={() => {
                setShowBcc(false);
                setBcc('');
              }}
              disabled={isSending}
              title="Remove Bcc"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>
          </div>
        )}

        <div className="compose-subject-section" style={{ position: 'relative' }}>
          <span className="compose-label">Subject</span>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={isMobileView ? 'Subject' : ''}
            className="compose-input subject-input"
            disabled={isSending}
          />
          {/* Color Picker Dialog - Below Subject */}
          {showColorPicker && (
            <div className="color-picker-dialog-below-subject">
              {(() => {
                const { grayscale, brightColors, mainGrid } = generateColorSwatches();
                return (
                  <>
                    <div className="color-picker-section">
                      <div className="color-picker-title">Background color</div>
                      <div className="color-picker-row">
                        {grayscale.map((color, index) => (
                          <button
                            key={`bg-gray-${index}`}
                            className={`color-swatch ${selectedBgColor === color ? 'selected' : ''}`}
                            style={{ backgroundColor: color }}
                            onClick={() => handleBgColorSelect(color)}
                            title={color}
                          >
                            {selectedBgColor === color && (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill={color === '#ffffff' ? '#000000' : '#ffffff'}>
                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                              </svg>
                            )}
                          </button>
                        ))}
                      </div>
                      <div className="color-picker-row">
                        {brightColors.map((color, index) => (
                          <button
                            key={`bg-bright-${index}`}
                            className={`color-swatch ${selectedBgColor === color ? 'selected' : ''}`}
                            style={{ backgroundColor: color }}
                            onClick={() => handleBgColorSelect(color)}
                            title={color}
                          >
                            {selectedBgColor === color && (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="#ffffff">
                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                              </svg>
                            )}
                          </button>
                        ))}
                      </div>
                      <div className="color-picker-grid">
                        {mainGrid.map((color, index) => (
                          <button
                            key={`bg-grid-${index}`}
                            className={`color-swatch ${selectedBgColor === color ? 'selected' : ''}`}
                            style={{ backgroundColor: color }}
                            onClick={() => handleBgColorSelect(color)}
                            title={color}
                          >
                            {selectedBgColor === color && (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="#ffffff">
                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                              </svg>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="color-picker-section">
                      <div className="color-picker-title">Text color</div>
                      <div className="color-picker-row">
                        {grayscale.map((color, index) => (
                          <button
                            key={`text-gray-${index}`}
                            className={`color-swatch ${selectedTextColor === color ? 'selected' : ''}`}
                            style={{ backgroundColor: color }}
                            onClick={() => handleTextColorSelect(color)}
                            title={color}
                          >
                            {selectedTextColor === color && (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill={color === '#000000' ? '#ffffff' : '#000000'}>
                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                              </svg>
                            )}
                          </button>
                        ))}
                      </div>
                      <div className="color-picker-row">
                        {brightColors.map((color, index) => (
                          <button
                            key={`text-bright-${index}`}
                            className={`color-swatch ${selectedTextColor === color ? 'selected' : ''}`}
                            style={{ backgroundColor: color }}
                            onClick={() => handleTextColorSelect(color)}
                            title={color}
                          >
                            {selectedTextColor === color && (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="#ffffff">
                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                              </svg>
                            )}
                          </button>
                        ))}
                      </div>
                      <div className="color-picker-grid">
                        {mainGrid.map((color, index) => (
                          <button
                            key={`text-grid-${index}`}
                            className={`color-swatch ${selectedTextColor === color ? 'selected' : ''}`}
                            style={{ backgroundColor: color }}
                            onClick={() => handleTextColorSelect(color)}
                            title={color}
                          >
                            {selectedTextColor === color && (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="#ffffff">
                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                              </svg>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>

        {/* Emoji Picker - Below Subject */}
        {showEmojiPicker && (
          <div className="emoji-picker-wrapper">
            <EmojiPicker
              onEmojiClick={(emojiObject) => {
                // Insert emoji into contentEditable div at saved cursor position
                if (bodyTextareaRef.current) {
                  // Focus the contentEditable div first
                  bodyTextareaRef.current.focus();
                  
                  const selection = window.getSelection();
                  let range;
                  
                  // Use saved cursor position if available, otherwise try current selection
                  if (savedCursorPositionRef.current) {
                    try {
                      // Validate that the saved range is still valid
                      range = savedCursorPositionRef.current.cloneRange();
                      // Check if the range is still within the contentEditable div
                      if (!bodyTextareaRef.current.contains(range.commonAncestorContainer)) {
                        // Range is invalid, create new one at end
                        range = document.createRange();
                        range.selectNodeContents(bodyTextareaRef.current);
                        range.collapse(false);
                      }
                    } catch (e) {
                      // Range is invalid, create new one at end
                      range = document.createRange();
                      range.selectNodeContents(bodyTextareaRef.current);
                      range.collapse(false);
                    }
                  } else if (selection.rangeCount > 0) {
                    // Use current selection if available
                    range = selection.getRangeAt(0);
                  } else {
                    // Create a new range at the end of the content
                    range = document.createRange();
                    range.selectNodeContents(bodyTextareaRef.current);
                    range.collapse(false);
                  }
                  
                  // Delete any selected content
                  range.deleteContents();
                  
                  // Insert the emoji at cursor position
                  const textNode = document.createTextNode(emojiObject.emoji);
                  range.insertNode(textNode);
                  
                  // Move cursor after the inserted emoji
                  range.setStartAfter(textNode);
                  range.setEndAfter(textNode);
                  selection.removeAllRanges();
                  selection.addRange(range);
                  
                  // Update saved cursor position to after the emoji
                  savedCursorPositionRef.current = range.cloneRange();
                  
                  // Update body state
                  setBody(bodyTextareaRef.current.innerHTML);
                  
                  // Trigger input event to ensure state is updated
                  const event = new Event('input', { bubbles: true });
                  bodyTextareaRef.current.dispatchEvent(event);
                  
                  // Keep focus on the contentEditable div
                  bodyTextareaRef.current.focus();
                } else {
                  // Fallback: append to body string
                  setBody(body + emojiObject.emoji);
                }
              }}
              width="450px"
              height="360px"
              searchPlaceHolder="Search"
              previewConfig={{
                showPreview: false
              }}
              skinTonesDisabled={false}
              lazyLoadEmojis={true}
              emojiStyle="native"
            />
          </div>
        )}

        {/* Insert Link Modal - Below Subject */}
        {showLinkModal && (
          <div className="link-modal-inline">
            <div className="link-modal-content">
              <div className="link-input-group">
                <div className="link-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>
                  </svg>
                </div>
                <input
                  type="text"
                  className="link-input"
                  placeholder="Text"
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="link-input-group">
                <div className="link-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>
                  </svg>
                </div>
                <input
                  type="url"
                  className="link-input"
                  placeholder="Type or paste a link"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                />
              </div>
              <button 
                className="link-apply-btn"
                onClick={() => {
                  if (linkText && linkUrl) {
                    const normalizedUrl = normalizeLinkUrl(linkUrl);
                    // Insert link into contentEditable div
                    if (bodyTextareaRef.current) {
                      const selection = window.getSelection();
                      if (selection.rangeCount > 0) {
                        const range = selection.getRangeAt(0);
                        range.deleteContents();
                        const linkElement = document.createElement('a');
                        linkElement.href = normalizedUrl;
                        linkElement.textContent = linkText;
                        linkElement.target = '_blank';
                        linkElement.rel = 'noopener noreferrer';
                        linkElement.style.color = '#1a73e8';
                        linkElement.style.textDecoration = 'underline';
                        range.insertNode(linkElement);
                        range.setStartAfter(linkElement);
                        range.setEndAfter(linkElement);
                        selection.removeAllRanges();
                        selection.addRange(range);
                        // Update body state
                        setBody(bodyTextareaRef.current.innerHTML);
                      } else {
                        // Fallback: append to end
                        const linkElement = document.createElement('a');
                        linkElement.href = normalizedUrl;
                        linkElement.textContent = linkText;
                        linkElement.target = '_blank';
                        linkElement.rel = 'noopener noreferrer';
                        linkElement.style.color = '#1a73e8';
                        linkElement.style.textDecoration = 'underline';
                        bodyTextareaRef.current.appendChild(linkElement);
                        setBody(bodyTextareaRef.current.innerHTML);
                      }
                    } else {
                      const link = `[${linkText}](${normalizedUrl})`;
                      setBody(body + link);
                    }
                    setLinkText('');
                    setLinkUrl('');
                    setShowLinkModal(false);
                  }
                }}
                disabled={!linkText || !linkUrl}
              >
                Apply
              </button>
            </div>
          </div>
        )}

        <div className="compose-body-section">
          <div
            ref={bodyTextareaRef}
            contentEditable
            onInput={(e) => {
              const html = e.target.innerHTML;
              setBody(html);
            }}
            onFocus={() => setIsBodyFocused(true)}
            onBlur={() => setIsBodyFocused(false)}
            className="compose-textarea"
            data-placeholder={isMobileView ? 'Compose email' : ''}
            suppressContentEditableWarning={true}
            style={{ 
              fontFamily: getFontFamily(selectedFont),
              fontSize: getFontSize(selectedTextSize)
            }}
          />
          {attachedFiles.length > 0 && (
            <div className="compose-attachments-section">
              {attachedFiles.map((file, index) => (
                <div key={index} className="attachment-item">
                  <div className="attachment-info">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="attachment-icon">
                      <path d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z"/>
                    </svg>
                    <div className="attachment-details">
                      <span className="attachment-name">{file.name}</span>
                      <span className="attachment-size">{formatFileSize(file.size)}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="attachment-remove-btn"
                    onClick={() => handleRemoveFile(index)}
                    disabled={isSending}
                    title="Remove attachment"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {message.text && (
          <div className={`compose-message ${message.type}`}>
            {message.text}
          </div>
        )}

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          multiple
          style={{ display: 'none' }}
        />

        {showFormatting && (
          <div className="formatting-toolbar">
            <div className="formatting-group">
              <button className="formatting-btn" title="Undo" disabled={isSending}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z"/>
                </svg>
              </button>
              <button className="formatting-btn" title="Redo" disabled={isSending}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.4 10.6C16.55 8.99 14.15 8 11.5 8c-4.65 0-8.58 3.03-9.96 7.22L3.9 16c1.05-3.19 4.05-5.5 7.6-5.5 1.95 0 3.73.72 5.12 1.88L13 16h9V7l-3.6 3.6z"/>
                </svg>
              </button>
            </div>
            
            <div className="formatting-divider"></div>
            
            <div className="formatting-group">
              <select 
                className="formatting-select font-select" 
                title="Font" 
                disabled={isSending} 
                value={selectedFont}
                onChange={(e) => setSelectedFont(e.target.value)}
              >
                <option value="Sans Serif" style={{fontFamily: 'Arial, Helvetica, sans-serif'}}>Sans Serif</option>
                <option value="Serif" style={{fontFamily: 'Georgia, "Times New Roman", serif'}}>Serif</option>
                <option value="Fixed Width" style={{fontFamily: '"Courier New", Courier, monospace'}}>Fixed Width</option>
                <option value="Wide" style={{fontFamily: 'Arial, sans-serif', fontStretch: 'expanded', fontWeight: '600'}}>Wide</option>
                <option value="Narrow" style={{fontFamily: 'Arial Narrow, sans-serif', fontStretch: 'condensed'}}>Narrow</option>
                <option value="Comic Sans MS" style={{fontFamily: '"Comic Sans MS", cursive'}}>Comic Sans MS</option>
                <option value="Garamond" style={{fontFamily: 'Garamond, serif'}}>Garamond</option>
                <option value="Georgia" style={{fontFamily: 'Georgia, serif'}}>Georgia</option>
                <option value="Tahoma" style={{fontFamily: 'Tahoma, Geneva, sans-serif'}}>Tahoma</option>
                <option value="Trebuchet MS" style={{fontFamily: '"Trebuchet MS", sans-serif'}}>Trebuchet MS</option>
                <option value="Verdana" style={{fontFamily: 'Verdana, Geneva, sans-serif'}}>Verdana</option>
              </select>
              
              <div className="text-size-dropdown-container" style={{ position: 'relative' }}>
                <button 
                  className={`formatting-btn text-size-btn ${showTextSizeDropdown ? 'active' : ''}`}
                  title="Text size"
                  disabled={isSending}
                  onClick={() => setShowTextSizeDropdown(!showTextSizeDropdown)}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    {/* Smaller T with line above - positioned above and to the left */}
                    <rect x="4" y="1" width="2" height="1.5" fill="currentColor"/>
                    <rect x="4" y="3" width="2" height="5" fill="currentColor"/>
                    <rect x="3" y="4" width="4" height="1.5" fill="currentColor"/>
                    {/* Larger T - positioned below and to the right */}
                    <rect x="10" y="5" width="7" height="2" fill="currentColor"/>
                    <rect x="12" y="5" width="3" height="10" fill="currentColor"/>
                  </svg>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" className="dropdown-arrow">
                    <path d="M7 10l5 5 5-5z"/>
                  </svg>
                </button>
                {showTextSizeDropdown && (
                  <div className="text-size-dropdown">
                    <button 
                      className={`text-size-option ${selectedTextSize === 'small' ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedTextSize('small');
                        setShowTextSizeDropdown(false);
                      }}
                    >
                      {selectedTextSize === 'small' ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-size-checkmark">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                        </svg>
                      ) : (
                        <span className="text-size-checkmark-placeholder"></span>
                      )}
                      <span>Small</span>
                    </button>
                    <button 
                      className={`text-size-option ${selectedTextSize === 'normal' ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedTextSize('normal');
                        setShowTextSizeDropdown(false);
                      }}
                    >
                      {selectedTextSize === 'normal' ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-size-checkmark">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                        </svg>
                      ) : (
                        <span className="text-size-checkmark-placeholder"></span>
                      )}
                      <span>Normal</span>
                    </button>
                    <button 
                      className={`text-size-option ${selectedTextSize === 'large' ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedTextSize('large');
                        setShowTextSizeDropdown(false);
                      }}
                    >
                      {selectedTextSize === 'large' ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-size-checkmark">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                        </svg>
                      ) : (
                        <span className="text-size-checkmark-placeholder"></span>
                      )}
                      <span>Large</span>
                    </button>
                    <button 
                      className={`text-size-option ${selectedTextSize === 'huge' ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedTextSize('huge');
                        setShowTextSizeDropdown(false);
                      }}
                    >
                      {selectedTextSize === 'huge' ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-size-checkmark">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                        </svg>
                      ) : (
                        <span className="text-size-checkmark-placeholder"></span>
                      )}
                      <span>Huge</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            <div className="formatting-divider"></div>
            
            <div className="formatting-group">
              <button 
                className="formatting-btn" 
                title="Bold (Ctrl+B)" 
                disabled={isSending}
                onClick={handleBold}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M15.6 10.79c.97-.67 1.65-1.77 1.65-2.79 0-2.26-1.75-4-4-4H7v14h7.04c2.09 0 3.71-1.7 3.71-3.79 0-1.52-.86-2.82-2.15-3.42zM10 6.5h3c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-3v-3zm3.5 9H10v-3h3.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5z"/>
                </svg>
              </button>
              <button 
                className="formatting-btn" 
                title="Italic (Ctrl+I)" 
                disabled={isSending}
                onClick={handleItalic}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 4v3h2.21l-3.42 8H6v3h8v-3h-2.21l3.42-8H18V4z"/>
                </svg>
              </button>
              <button 
                className="formatting-btn" 
                title="Underline (Ctrl+U)" 
                disabled={isSending}
                onClick={handleUnderline}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 17c3.31 0 6-2.69 6-6V3h-2.5v8c0 1.93-1.57 3.5-3.5 3.5S8.5 12.93 8.5 11V3H6v8c0 3.31 2.69 6 6 6zm-7 2v2h14v-2H5z"/>
                </svg>
              </button>
            </div>
            
            <div className="formatting-divider"></div>
            
            <div className="formatting-group">
              <div className="color-picker-container" style={{ position: 'relative' }}>
                <button 
                  className={`formatting-btn ${showColorPicker ? 'active' : ''}`}
                  title="Text color" 
                  disabled={isSending}
                  onClick={() => setShowColorPicker(!showColorPicker)}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11 3L5.5 17h2.25l1.12-3h6.25l1.12 3h2.25L13 3h-2zm-1.38 9L12 5.67 14.38 12H9.62z"/>
                    <rect x="3" y="20" width="18" height="3" fill="#1a73e8"/>
                  </svg>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" className="dropdown-arrow">
                    <path d="M7 10l5 5 5-5z"/>
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="formatting-divider"></div>
            
            <div className="formatting-group">
              <div className="alignment-dropdown-container" style={{ position: 'relative' }}>
                <button 
                  className={`formatting-btn alignment-btn ${showAlignmentDropdown ? 'active' : ''}`}
                  title="Align left" 
                  disabled={isSending}
                  onClick={() => setShowAlignmentDropdown(!showAlignmentDropdown)}
                >
                  {selectedAlignment === 'left' && (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3 3h18v1.5H3V3zm0 3.5h14v1.5H3V6.5zm0 3.5h18v1.5H3V10zm0 3.5h14v1.5H3v-1.5z"/>
                    </svg>
                  )}
                  {selectedAlignment === 'center' && (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3 3h18v1.5H3V3zm2.5 3.5h13v1.5h-13V6.5zm-2.5 3.5h18v1.5H3V10zm2.5 3.5h13v1.5h-13v-1.5z"/>
                    </svg>
                  )}
                  {selectedAlignment === 'right' && (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3 3h18v1.5H3V3zm4 3.5h14v1.5H7V6.5zm-4 3.5h18v1.5H3V10zm4 3.5h14v1.5H7v-1.5z"/>
                    </svg>
                  )}
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" className="dropdown-arrow">
                    <path d="M7 10l5 5 5-5z"/>
                  </svg>
                </button>
                {showAlignmentDropdown && (
                  <div className="alignment-dropdown">
                    <button 
                      className={`alignment-option ${selectedAlignment === 'left' ? 'selected' : ''}`}
                      onClick={() => handleAlignment('left')}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3 3h18v1.5H3V3zm0 3.5h14v1.5H3V6.5zm0 3.5h18v1.5H3V10zm0 3.5h14v1.5H3v-1.5z"/>
                      </svg>
                    </button>
                    <button 
                      className={`alignment-option ${selectedAlignment === 'center' ? 'selected' : ''}`}
                      onClick={() => handleAlignment('center')}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3 3h18v1.5H3V3zm2.5 3.5h13v1.5h-13V6.5zm-2.5 3.5h18v1.5H3V10zm2.5 3.5h13v1.5h-13v-1.5z"/>
                      </svg>
                    </button>
                    <button 
                      className={`alignment-option ${selectedAlignment === 'right' ? 'selected' : ''}`}
                      onClick={() => handleAlignment('right')}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3 3h18v1.5H3V3zm4 3.5h14v1.5H7V6.5zm-4 3.5h18v1.5H3V10zm4 3.5h14v1.5H7v-1.5z"/>
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            <div className="formatting-divider"></div>
            
            <div className="formatting-group">
              <button 
                className="formatting-btn" 
                title="Numbered list" 
                disabled={isSending}
                onClick={handleNumberedList}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M2 17h2v.5H3v1h1v.5H2v1h3v-4H2v1zm1-9h1V4H2v1h1v3zm-1 3h1.8L2 13.1v.9h3v-1H3.2L5 10.9V10H2v1zm5-6v2h14V5H7zm0 14h14v-2H7v2zm0-6h14v-2H7v2z"/>
                </svg>
              </button>
            </div>
            
            <div className="formatting-divider"></div>
            
            <div className="formatting-group">
              <div className="formatting-more-options-container" style={{ position: 'relative' }}>
                <button 
                  className={`formatting-btn ${showFormattingMoreOptions ? 'active' : ''}`}
                  title="More options" 
                  disabled={isSending}
                  onClick={() => setShowFormattingMoreOptions(!showFormattingMoreOptions)}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="6" cy="12" r="2"/>
                    <circle cx="12" cy="12" r="2"/>
                    <circle cx="18" cy="12" r="2"/>
                  </svg>
                </button>
                {showFormattingMoreOptions && (
                  <div className="formatting-more-options-dropdown">
                    <button 
                      className="formatting-more-options-item"
                      title="Strikethrough"
                      onClick={() => {
                        handleStrikethrough();
                        setShowFormattingMoreOptions(false);
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M7.24 8l1.35-1.68C9.24 5.56 9.95 5 10.94 5c.21 0 .39.03.55.1.3.11.57.29.79.51l.12.13c.2.21.36.42.5.65.13.23.23.48.3.75.07.27.1.55.1.84 0 .64-.13 1.21-.39 1.72-.26.51-.64.94-1.13 1.28L9.5 12.1c-.19-.13-.35-.28-.48-.45-.13-.17-.23-.36-.3-.57-.07-.21-.1-.43-.1-.66 0-.24.03-.47.09-.69.06-.22.15-.42.27-.6l.01-.01zm8.52 0l-1.35-1.68C13.76 5.56 13.05 5 12.06 5c-.21 0-.39.03-.55.1-.3.11-.57.29-.79.51l-.12.13c-.2.21-.36.42-.5.65-.13.23-.23.48-.3.75-.07.27-.1.55-.1.84 0 .64.13 1.21.39 1.72.26.51.64.94 1.13 1.28l1.27.95c.19.13.35.28.48.45.13.17.23.36.3.57.07.21.1.43.1.66 0 .24-.03.47-.09.69-.06.22-.15.42-.27.6l-.01.01zM3 13h18v-2H3v2z"/>
                      </svg>
                    </button>
                    <button 
                      className="formatting-more-options-item"
                      title="Clear formatting"
                      onClick={() => {
                        document.execCommand('removeFormat', false, null);
                        bodyTextareaRef.current?.focus();
                        setShowFormattingMoreOptions(false);
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none"/>
                        <line x1="8" y1="12" x2="16" y2="12" stroke="currentColor" strokeWidth="2"/>
                        <line x1="12" y1="8" x2="12" y2="16" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </button>
                    <button 
                      className="formatting-more-options-item"
                      title="Subscript/Superscript"
                      onClick={() => {
                        handleSuperscript();
                        setShowFormattingMoreOptions(false);
                      }}
                    >
                      <span style={{ fontSize: '12px', fontWeight: 'bold', lineHeight: '1' }}>99</span>
                    </button>
                    <button 
                      className="formatting-more-options-item"
                      title="Increase indent"
                      onClick={() => {
                        handleIncreaseIndent();
                        setShowFormattingMoreOptions(false);
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3 3h18v1.5H3V3zm0 3.5h14v1.5H3V6.5zm0 3.5h18v1.5H3V10zm0 3.5h14v1.5H3v-1.5zm0 3.5h18v1.5H3V17z"/>
                        <path d="M5 6.5h2v1.5H5V6.5zm0 7h2v1.5H5v-1.5z" fill="currentColor" opacity="0.3"/>
                        <path d="M3 6.5l2 1.5v-3L3 6.5z" fill="currentColor"/>
                      </svg>
                    </button>
                    <button 
                      className="formatting-more-options-item"
                      title="Decrease indent"
                      onClick={() => {
                        handleDecreaseIndent();
                        setShowFormattingMoreOptions(false);
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3 3h18v1.5H3V3zm0 3.5h14v1.5H3V6.5zm0 3.5h18v1.5H3V10zm0 3.5h14v1.5H3v-1.5zm0 3.5h18v1.5H3V17z"/>
                        <path d="M3 6.5h2v1.5H3V6.5zm0 7h2v1.5H3v-1.5z" fill="currentColor" opacity="0.3"/>
                        <path d="M1 6.5l2 1.5v-3L1 6.5z" fill="currentColor"/>
                      </svg>
                    </button>
                    <button 
                      className="formatting-more-options-item"
                      title="Bulleted list"
                      onClick={() => {
                        handleBulletList();
                        setShowFormattingMoreOptions(false);
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M4 10.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zm0-6c-.83 0-1.5.67-1.5 1.5S3.17 7.5 4 7.5 5.5 6.83 5.5 6 4.83 4.5 4 4.5zm0 12c-.83 0-1.5.68-1.5 1.5s.68 1.5 1.5 1.5 1.5-.68 1.5-1.5-.67-1.5-1.5-1.5zM7 19h14v-2H7v2zm0-6h14v-2H7v2zm0-8v2h14V5H7z"/>
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {!isMobileView && (
        <div className="compose-toolbar">
          <div className="toolbar-left">
            <div className="send-options-container" style={{ position: 'relative' }}>
              <button
                onClick={handleSend}
                disabled={isSending || (sendToast && sendToast.status === 'sending')}
                className="send-btn"
              >
                {isSending ? 'Sending...' : 'Send'}
              </button>
              <button 
                className="send-options-btn"
                onClick={() => setShowSendOptions(!showSendOptions)}
                title="Send options"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7 10l5 5 5-5z"/>
                </svg>
              </button>
              {showSendOptions && (
                <div className="send-options-dropdown">
                  <button
                    className="send-option-item"
                    onClick={handleScheduleSend}
                    disabled={isSending}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '8px' }}>
                      <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
                    </svg>
                    Schedule send
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="toolbar-right">
            <button 
              className={`toolbar-icon-btn ${showFormatting ? 'active' : ''}`}
              title="Formatting options"
              onClick={() => setShowFormatting(!showFormatting)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M5 18h14v2H5v-2zm0-8h14v2H5v-2zm2-4h10v2H7V6z"/>
              </svg>
            </button>
            <button 
              className="toolbar-icon-btn" 
              title="Attach files"
              onClick={handleAttachFiles}
              disabled={isSending}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z"/>
              </svg>
            </button>
            <button 
              className="toolbar-icon-btn" 
              title="Insert link"
              onClick={() => setShowLinkModal(!showLinkModal)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>
              </svg>
            </button>
            <button 
              className={`toolbar-icon-btn ${showEmojiPicker ? 'active' : ''}`}
              title="Add emoji"
              onClick={() => {
                // Save cursor position before opening emoji picker
                if (bodyTextareaRef.current) {
                  const selection = window.getSelection();
                  if (selection.rangeCount > 0) {
                    savedCursorPositionRef.current = selection.getRangeAt(0).cloneRange();
                  } else {
                    // Save position at end if no selection
                    const range = document.createRange();
                    range.selectNodeContents(bodyTextareaRef.current);
                    range.collapse(false);
                    savedCursorPositionRef.current = range;
                  }
                }
                setShowEmojiPicker(!showEmojiPicker);
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="1.5"/>
                <circle cx="8.5" cy="10.5" r="1.5" fill="currentColor"/>
                <circle cx="15.5" cy="10.5" r="1.5" fill="currentColor"/>
                <path d="M12 18c2.28 0 4.22-1.66 5-4H7c.78 2.34 2.72 4 5 4z" fill="currentColor"/>
              </svg>
            </button>
            <button 
              className={`toolbar-icon-btn ${showInsertPhotoModal ? 'active' : ''}`}
              title="Insert photo"
              onClick={() => setShowInsertPhotoModal(true)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
              </svg>
            </button>
            <button 
              className={`toolbar-icon-btn ${showConfidentialModal ? 'active' : ''}`}
              title="Confidential mode"
              onClick={() => setShowConfidentialModal(true)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
              </svg>
            </button>
            <div className="signature-dropdown-container" style={{ position: 'relative' }}>
              <button 
                className={`toolbar-icon-btn ${showSignatureDropdown ? 'active' : ''}`}
                title="Insert signature"
                onClick={() => setShowSignatureDropdown(!showSignatureDropdown)}
              >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
              </svg>
            </button>
              {showSignatureDropdown && (
                <div className="signature-dropdown">
                  <button
                    className="signature-dropdown-item"
                    onClick={() => {
                      setShowSignatureDropdown(false);
                      navigate('/account');
                    }}
                  >
                    Manage signatures
                  </button>
                  <div className="signature-dropdown-divider"></div>
                  {signatureText.trim() && (
                    <button
                      className={`signature-dropdown-item ${selectedSignature === 'my' ? 'selected' : ''}`}
                      onClick={applySignature}
                    >
                      {selectedSignature === 'my' && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="signature-checkmark">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                        </svg>
                      )}
                      My signature
                    </button>
                  )}
                  <button
                    className={`signature-dropdown-item ${selectedSignature === 'no-signature' ? 'selected' : ''}`}
                    onClick={clearSignature}
                  >
                    {selectedSignature === 'no-signature' && (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="signature-checkmark">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                      </svg>
                    )}
                    No signature
                  </button>
                </div>
              )}
            </div>
            <div className="more-options-dropdown-container" style={{ position: 'relative' }}>
              <button 
                className={`toolbar-icon-btn ${showMoreOptionsDropdown ? 'active' : ''}`}
                title="More options"
                onClick={() => setShowMoreOptionsDropdown(!showMoreOptionsDropdown)}
              >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="5" r="2"/>
                <circle cx="12" cy="12" r="2"/>
                <circle cx="12" cy="19" r="2"/>
              </svg>
            </button>
              {showMoreOptionsDropdown && renderComposeMoreMenu()}
            </div>
            <button className="toolbar-icon-btn" onClick={handleDiscard} title="Discard">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
              </svg>
            </button>
          </div>
        </div>
        )}

      </div>

      <ConfirmationModal
        isOpen={showDiscardConfirm}
        onClose={() => setShowDiscardConfirm(false)}
        onConfirm={confirmDiscard}
        message="Discard this draft?"
        confirmText="OK"
        cancelText="Cancel"
      />

      {/* Schedule Send Modal */}
      {showScheduleModal && (
        <div className="modal-overlay schedule-modal-overlay" onClick={() => setShowScheduleModal(false)}>
          <div className="schedule-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="schedule-modal-header">
              <h3>Schedule send</h3>
              <button 
                className="schedule-modal-close"
                onClick={() => setShowScheduleModal(false)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>
            <div className="schedule-modal-body">
              <div className="schedule-date-time-picker">
                <div className="schedule-picker-group">
                  <label className="schedule-label">Pick date & time</label>
                  <div className="schedule-inputs">
                    <input
                      type="date"
                      className="schedule-date-input"
                      value={scheduleDate}
                      onChange={(e) => setScheduleDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                    />
                    <input
                      type="time"
                      className="schedule-time-input"
                      value={scheduleTime}
                      onChange={(e) => setScheduleTime(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="schedule-modal-footer">
              <button 
                className="schedule-btn schedule-btn-cancel"
                onClick={() => setShowScheduleModal(false)}
              >
                Cancel
              </button>
              <button 
                className="schedule-btn schedule-btn-confirm"
                onClick={handleScheduleConfirm}
                disabled={isSending || !scheduleDate || !scheduleTime}
              >
                Schedule send
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confidential Mode Modal */}
      {showConfidentialModal && (
        <div className="modal-overlay" onClick={() => setShowConfidentialModal(false)}>
          <div className="confidential-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="confidential-modal-header">
              <h3>Confidential mode</h3>
              <button 
                className="confidential-modal-close"
                onClick={() => setShowConfidentialModal(false)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>
            <div className="confidential-modal-body">
              <p className="confidential-description">
                Recipients won't have the option to forward, copy, print, or download this email.
              </p>
              <a href="#" className="confidential-learn-more" onClick={(e) => e.preventDefault()}>
                Learn more
              </a>

              <div className="confidential-section">
                <h4 className="confidential-section-title">SET EXPIRATION</h4>
                <div className="confidential-expiration-group">
                  <select
                    className="confidential-expiration-select"
                    value={confidentialExpiration}
                    onChange={(e) => setConfidentialExpiration(e.target.value)}
                  >
                    <option value="1 day">Expires in 1 day</option>
                    <option value="3 days">Expires in 3 days</option>
                    <option value="1 week">Expires in 1 week</option>
                    <option value="1 month">Expires in 1 month</option>
                    <option value="3 months">Expires in 3 months</option>
                    <option value="5 years">Expires in 5 years</option>
                  </select>
                  <span className="confidential-expiration-date">
                    {(() => {
                      const date = new Date();
                      const days = {
                        '1 day': 1,
                        '3 days': 3,
                        '1 week': 7,
                        '1 month': 30,
                        '3 months': 90,
                        '5 years': 1825
                      };
                      date.setDate(date.getDate() + (days[confidentialExpiration] || 7));
                      return date.toLocaleDateString('en-US', { 
                        weekday: 'short', 
                        month: 'short', 
                        day: 'numeric', 
                        year: 'numeric' 
                      });
                    })()}
                  </span>
                </div>
              </div>

              <div className="confidential-section">
                <h4 className="confidential-section-title">REQUIRE PASSCODE</h4>
                <div className="confidential-passcode-info">
                  <span>All passcodes will be generated by Google.</span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="info-icon">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                  </svg>
                </div>
                <div className="confidential-radio-group">
                  <label className="confidential-radio-label">
                    <input
                      type="radio"
                      name="passcode"
                      value="no-sms"
                      checked={confidentialPasscode === 'no-sms'}
                      onChange={(e) => setConfidentialPasscode(e.target.value)}
                      className="confidential-radio"
                    />
                    <span>No SMS passcode</span>
                  </label>
                  <label className="confidential-radio-label">
                    <input
                      type="radio"
                      name="passcode"
                      value="sms"
                      checked={confidentialPasscode === 'sms'}
                      onChange={(e) => setConfidentialPasscode(e.target.value)}
                      className="confidential-radio"
                    />
                    <span>SMS passcode</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="confidential-modal-footer">
              <button 
                className="confidential-btn-cancel"
                onClick={() => setShowConfidentialModal(false)}
              >
                Cancel
              </button>
              <button 
                className="confidential-btn-save"
                onClick={() => {
                  // Handle save confidential mode settings
                  setShowConfidentialModal(false);
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Insert Photo Modal */}
      {showInsertPhotoModal && (
        <div className="modal-overlay" onClick={() => setShowInsertPhotoModal(false)}>
          <div className="insert-photo-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="insert-photo-modal-header">
              <h3>Insert Photo</h3>
              <div className="insert-photo-search-container">
                <input
                  type="text"
                  className="insert-photo-search"
                  placeholder="Search for photos"
                  value={photoSearch}
                  onChange={(e) => setPhotoSearch(e.target.value)}
                />
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="search-icon">
                  <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                </svg>
              </div>
              <button 
                className="insert-photo-modal-close"
                onClick={() => setShowInsertPhotoModal(false)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>

            <div className="insert-photo-tabs">
              <button
                className={`insert-photo-tab ${photoTab === 'google-photos' ? 'active' : ''}`}
                onClick={() => setPhotoTab('google-photos')}
              >
                Google Photos
              </button>
              <button
                className={`insert-photo-tab ${photoTab === 'upload' ? 'active' : ''}`}
                onClick={() => setPhotoTab('upload')}
              >
                Upload
              </button>
              <button
                className={`insert-photo-tab ${photoTab === 'url' ? 'active' : ''}`}
                onClick={() => setPhotoTab('url')}
              >
                Web Address (URL)
              </button>
            </div>

            <div className="insert-photo-modal-body">
              {photoTab === 'google-photos' && (
                <>
                  <div className="insert-photo-sidebar">
                    <button
                      className={`insert-photo-sidebar-item ${photoSubTab === 'photos' ? 'active' : ''}`}
                      onClick={() => setPhotoSubTab('photos')}
                    >
                      Photos
                    </button>
                    <button
                      className={`insert-photo-sidebar-item ${photoSubTab === 'albums' ? 'active' : ''}`}
                      onClick={() => setPhotoSubTab('albums')}
                    >
                      Albums
                    </button>
                  </div>
                  <div className="insert-photo-content">
                    <div className="insert-photo-empty">
                      <svg width="120" height="120" viewBox="0 0 120 120" fill="none" className="insert-photo-empty-icon">
                        {/* Yellow magnifying glass */}
                        <circle cx="50" cy="50" r="35" fill="#FDD663" stroke="#F9AB00" strokeWidth="2"/>
                        {/* Red dot on handle */}
                        <circle cx="75" cy="75" r="6" fill="#EA4335"/>
                        {/* Blue curved lines inside lens */}
                        <path d="M35 35 Q50 45 65 35" stroke="#4285F4" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
                        <path d="M35 45 Q50 55 65 45" stroke="#4285F4" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
                        <path d="M35 55 Q50 65 65 55" stroke="#4285F4" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
                        <path d="M35 65 Q50 75 65 65" stroke="#4285F4" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
                      </svg>
                      <p className="insert-photo-empty-title">No photos were found in your account.</p>
                      <p className="insert-photo-empty-text">Please add photos and try reloading.</p>
                    </div>
                  </div>
                </>
              )}

              {photoTab === 'upload' && (
                <div className="insert-photo-upload">
                  <div className="insert-photo-upload-area">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor" className="upload-icon">
                      <path d="M19 12v7H5v-7H3v7c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2zm-6 .67l2.59-2.58L17 11.5l-5 5-5-5 1.41-1.41L11 12.67V3h2z"/>
                    </svg>
                    <p>Drag and drop photos here or click to browse</p>
                  </div>
                </div>
              )}

              {photoTab === 'url' && (
                <div className="insert-photo-url">
                  <input
                    type="url"
                    className="insert-photo-url-input"
                    placeholder="Enter image URL"
                  />
                  <button className="insert-photo-url-btn">Insert</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComposeMail;
